import { HttpResponse, HttpErrorResponse, HTTP_INTERCEPTORS, HttpClient, HttpParams, HttpHeaders, HttpRequest } from '@angular/common/http';
import { ɵɵdefineInjectable, ɵsetClassMetadata, Injectable, InjectionToken, ɵɵinject, Optional, Inject, APP_INITIALIZER, Injector, ɵɵdefineNgModule, ɵɵdefineInjector, NgModule, SkipSelf } from '@angular/core';
import { of, throwError } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';

/* tslint:disable */
/**
 * Global configuration
 */
class ApiConfiguration {
    constructor() {
        this.rootUrl = '';
    }
}
ApiConfiguration.ɵfac = function ApiConfiguration_Factory(t) { return new (t || ApiConfiguration)(); };
ApiConfiguration.ɵprov = ɵɵdefineInjectable({ token: ApiConfiguration, factory: ApiConfiguration.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ApiConfiguration, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], null, null); })();

/**
 * `Mockup` data object's injection token.
 */
const MOCKUP = new InjectionToken("mockup");
/**
 * Supported HTTP methods
 */
var HttpMethod;
(function (HttpMethod) {
    HttpMethod["GET"] = "GET";
    HttpMethod["PATCH"] = "PATCH";
    HttpMethod["POST"] = "POST";
    HttpMethod["PUT"] = "PUT";
    HttpMethod["DELETE"] = "DELETE";
})(HttpMethod || (HttpMethod = {}));
/**
 * Supported HTTP status codes
 */
var HttpStatusCode;
(function (HttpStatusCode) {
    HttpStatusCode[HttpStatusCode["Success"] = 200] = "Success";
    HttpStatusCode[HttpStatusCode["BadRequest"] = 400] = "BadRequest";
    HttpStatusCode[HttpStatusCode["Unauthorized"] = 401] = "Unauthorized";
    HttpStatusCode[HttpStatusCode["NotFound"] = 404] = "NotFound";
    HttpStatusCode[HttpStatusCode["MethodNotAllowed"] = 422] = "MethodNotAllowed";
    HttpStatusCode[HttpStatusCode["ServerError"] = 500] = "ServerError";
})(HttpStatusCode || (HttpStatusCode = {}));
/**
 * Intercepts requests made to the Self-Service API and returns mockup data as response.
 *
 * The mocked data to return is declared in the `self-service.mockup.json` file of the package root directory.
 *
 * When new data has been added, make sure the package is rebuilt for the changes to take place.
 */
class SelfServiceMockupInterceptor {
    constructor(mockup) {
        this.mockup = mockup;
        // Throw an error if initialized without a mockup object provided
        if (!this.mockup)
            throw new Error("Could not initialize 'SelfServiceMockupInterceptor'. No mockup object was passed to the constructor.");
        // Set the default response time to 500 milliseconds
        if (this.mockup.delay == null)
            this.mockup.delay = 500;
    }
    /**
     * Intercept HTTP request and return appropriate HTTP response
     * @param request HTTP request object
     * @param next HTTP request handler
     */
    intercept(request, next) {
        const { method, url } = request;
        const matchedUrlPath = Object.keys(this.mockup.paths).find((path) => this.matchUrlPath(path, url));
        if (!matchedUrlPath)
            return next.handle(request);
        const params = this.parseParams(matchedUrlPath, url);
        if (!this.mockup.paths[matchedUrlPath][method]) {
            console.warn(`Could not intercept the request '${method} ${url}'. No mockup data exist for this path and method.`);
            return this.httpResponse(HttpStatusCode.MethodNotAllowed);
        }
        // Filter out objects in the mockup array that matches the parameters provided in the request URL
        // and remove all properties with an underscore from the response body
        const body = (this.mockup.paths[matchedUrlPath][method] || [])
            .filter(this.matchIdentifier(params))
            .map(this.removeUnderscoreProperties);
        if (body.length)
            if (body.length === 1 && body[0].$array !== true)
                // Return an object in case only a single item match with the provided path and parameters
                // and the $array custom property has not been defined
                return this.httpResponse(HttpStatusCode.Success, body.map(this.removeDollarProperties)[0]);
            else
                return this.httpResponse(HttpStatusCode.Success, body.map(this.removeDollarProperties));
        return this.httpResponse(HttpStatusCode.NotFound);
    }
    /**
     * Simulate HTTP response
     * @param status HTTP response status code
     * @param body HTTP response body
     */
    httpResponse(status, body) {
        if (status >= 200 && status <= 300)
            return of(new HttpResponse({ status, body })).pipe(delay(this.mockup.delay));
        else {
            return throwError(new HttpErrorResponse({
                error: `Error ${status}`,
                status,
            })).pipe(delay(this.mockup.delay));
        }
    }
    /**
     * Removes properties with an underscore from response body
     * @param body The body object to remove the properties from
     */
    removeUnderscoreProperties(body) {
        return Object.fromEntries(Object.entries(body).filter(([key, _]) => !key.startsWith("_")));
    }
    /**
     * Removes properties with an dollar symbol from response body
     * @param body The body object to remove the properties from
     */
    removeDollarProperties(body) {
        return Object.fromEntries(Object.entries(body).filter(([key, _]) => !key.startsWith("$")));
    }
    /**
     * Match provided resource identifiers or query parameters against data items in `mockup` array. Will match data items which does not contain properties that might be specified in `params`.
     * @param params Parameters to match
     */
    matchIdentifier(params = {}) {
        return (item = {}) => Object.entries(params)
            // Testing for loose equality to match numeric values cast as strings, i.e. "123"
            .reduce((match, [key, value]) => (!item[key] || item[key] == value ? match && true : false), true);
    }
    /**
     * Compares request URL with specified path matcher string
     * @param pathMatcher path matcher pattern to compare to the url
     * @param url url for the current request.
     */
    matchUrlPath(pathMatcher, url) {
        const matcherSegments = pathMatcher.split("/");
        const urlSegments = new URL(url, "https://localhost:4200").pathname.split("/");
        // Do not match URL's that have more segments than declared in pathMatcher
        if (matcherSegments.length < urlSegments.length)
            return false;
        // Do not match if any of the URL segments does not match the segments in pathMatcher
        for (let i = 0; i < matcherSegments.length; i++) {
            if (!matcherSegments[i].startsWith(":"))
                if (matcherSegments[i] !== urlSegments[i])
                    return false;
        }
        return true;
    }
    /**
     * Takes an URL and parse it against a matching string. Returns an object with the resource name or query parameter name as key and resource identifier query parameter value as value.
     * @param pathMatcher path matcher pattern to compare to the url
     * @param url url for the request
     *
     * **Example**
     *
     * This input:
     *
     * * path: `component/:componentId`
     * * url: `component/123&queryParam=abc`
     *
     * Will give this output:
     *
     * ```
     * { _componentId: "123", _queryParam: "abc" }
     * ```
     */
    parseParams(pathMatcher, url) {
        const urlSegments = new URL(url, "https://localhost:4200").pathname
            .split("/")
            .map((path, index) => [
            new URL(pathMatcher, "https://localhost:4200").pathname.split("/")[index].replace(":", "_"),
            path,
        ])
            .filter(([key, value]) => key !== value);
        const urlSearchParams = Array.from(new URLSearchParams(new URL(url, "https://localhost:4200").searchParams).entries()).map(([key, value]) => ["_" + key, value]);
        if (urlSegments.length || urlSearchParams.length)
            return Object.fromEntries([...urlSegments, ...urlSearchParams]);
    }
}
SelfServiceMockupInterceptor.ɵfac = function SelfServiceMockupInterceptor_Factory(t) { return new (t || SelfServiceMockupInterceptor)(ɵɵinject(MOCKUP, 8)); };
SelfServiceMockupInterceptor.ɵprov = ɵɵdefineInjectable({ token: SelfServiceMockupInterceptor, factory: SelfServiceMockupInterceptor.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(SelfServiceMockupInterceptor, [{
        type: Injectable
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [MOCKUP]
            }] }]; }, null); })();

/* tslint:disable */
/* eslint-disable */
const MOCKUP_DATA = {
    "$schema": "./self-service.schema.json",
    "delay": 500,
    "paths": {
        "/elements/my-premises-select": {
            "GET": [
                {
                    "id": "1",
                    "name": "Vattenledningsvägen 23H",
                    "zipCode": "126 33",
                    "city": "Hägersten",
                    "serviceGroup": "Electricity"
                },
                {
                    "id": "2",
                    "name": "Villavägen 6",
                    "zipCode": "168 53",
                    "city": "Bromma",
                    "serviceGroup": "Heat"
                },
                {
                    "id": "3",
                    "name": "Stengatan 98",
                    "zipCode": "668 30",
                    "city": "Sundsvall",
                    "serviceGroup": "Unknown"
                },
                {
                    "id": "TARIFFWITHFUTURE",
                    "name": "TARIFFWITHFUTURE",
                    "zipCode": "123 45",
                    "city": "TARI ST",
                    "serviceGroup": "electricity"
                },
                {
                    "id": "TARIFFWITHFUTURE",
                    "name": "TARIFFWITHFUTURE",
                    "zipCode": "123 45",
                    "city": "TARI ST",
                    "serviceGroup": "electricity"
                },
                {
                    "id": "FIXEDSE3Y",
                    "name": "FIXEDSE3Y",
                    "zipCode": "123 45",
                    "city": "FIXE ST",
                    "serviceGroup": "electricity"
                },
                {
                    "id": "FIXEDSE1Y",
                    "name": "FIXEDSE1Y",
                    "zipCode": "123 45",
                    "city": "FIXE ST",
                    "serviceGroup": "electricity"
                },
                { "id": "HEAT", "name": "HEAT", "zipCode": "123 45", "city": "HEAT ST", "serviceGroup": "heat" },
                { "id": "SPOT", "name": "SPOT", "zipCode": "123 45", "city": "SPOT ST", "serviceGroup": "electricity" },
                {
                    "id": "TIMETARIFF",
                    "name": "TIMETARIFF",
                    "zipCode": "123 45",
                    "city": "TIME ST",
                    "serviceGroup": "electricity"
                },
                { "id": "UNKNOWN", "name": "UNKNOWN", "zipCode": "123 45", "city": "UNKN ST", "serviceGroup": "electricity" },
                {
                    "id": "WINTERINSURANCE",
                    "name": "WINTERINSURANCE",
                    "zipCode": "123 45",
                    "city": "WINT ST",
                    "serviceGroup": "electricity"
                }
            ]
        },
        "/elements/my-widgets-premise-details/premises/:premiseId": {
            "GET": [
                {
                    "_premiseId": "1",
                    "customerId": "1000071093",
                    "premiseSiteName": "Dragontorpsvägen 7",
                    "premiseId": "AKB735999114009345270",
                    "premiseServiceGroup": "Electricity"
                },
                {
                    "_premiseId": "2",
                    "customerId": "1000071222",
                    "premiseSiteName": "Vikavägen 7",
                    "premiseId": "AKB735999435609345270",
                    "premiseServiceGroup": "Heat"
                },
                {
                    "_premiseId": "3",
                    "customerId": "1000071467",
                    "premiseSiteName": "Valhallaskogen 3",
                    "premiseId": "AKB735999114009345270",
                    "premiseServiceGroup": "Unknown"
                },
                {
                    "_premiseId": "4",
                    "customerId": "1000071467",
                    "premiseSiteName": "Upplandsgatan 34",
                    "premiseId": "AKB735999114009345270",
                    "premiseServiceGroup": "Electricity"
                }
            ]
        },
        "/elements/my-widgets-invoice-overview/premises/:premiseId": {
            "GET": [
                {
                    "_premiseId": "1",
                    "amount": 298.58,
                    "status": "Unpaid"
                },
                {
                    "_premiseId": "2",
                    "amount": 428.32,
                    "status": "PassedDueDate"
                },
                {
                    "_premiseId": "3",
                    "amount": 4325.55,
                    "status": "Paid"
                },
                {
                    "_premiseId": "4",
                    "amount": 0.0,
                    "contractState": "Preview"
                }
            ]
        },
        "/elements/my-widgets-consumption-overview/premises/:premiseId": {
            "GET": [
                {
                    "_premiseId": "1",
                    "year": 2020,
                    "month": 4,
                    "consumption": {
                        "value": 143,
                        "unit": "KWH"
                    },
                    "percentageChangeComparedToSameMonthPreviousYear": -1.4,
                    "serviceGroup": "Electricity"
                },
                {
                    "_premiseId": "2",
                    "year": 2019,
                    "month": 12,
                    "consumption": {
                        "value": 143,
                        "unit": "KWH"
                    },
                    "percentageChangeComparedToSameMonthPreviousYear": 10,
                    "serviceGroup": "Heat"
                },
                {
                    "_premiseId": "3",
                    "year": 2019,
                    "month": 12,
                    "consumption": {
                        "value": 143,
                        "unit": "KWH"
                    },
                    "percentageChangeComparedToSameMonthPreviousYear": 10,
                    "serviceGroup": "Heat"
                },
                {
                    "_premiseId": "4",
                    "contractState": "Preview"
                }
            ]
        },
        "/elements/my-widgets-contract-price/premises/:premiseId": {
            "GET": [
                {
                    "$array": true,
                    "_premiseId": "1",
                    "energyPrice": {
                        "value": 12.22,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "contractType": "Heat",
                    "contractName": "Värme – Villa"
                },
                {
                    "$array": true,
                    "_premiseId": "2",
                    "highElectricityPrice": {
                        "value": 10.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "lowElectricityPrice": {
                        "value": 9.05,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "contractType": "TimeTariffPrice",
                    "contractName": "Tidstariff"
                },
                {
                    "$array": true,
                    "_premiseId": "3",
                    "contractType": "UnknownElectricityContract",
                    "contractName": "Okändt elektricitetskontrakt"
                },
                {
                    "$array": true,
                    "_premiseId": "4",
                    "contractName": "Rörligt elpris",
                    "contractType": "SpotPrice",
                    "contractState": "Preview",
                    "contractStartDate": 1661983200.0
                }
            ]
        },
        "/elements/flows-energy-source-select-renewal-section": {
            "GET": [
                {
                    "_premiseId": "1",
                    "energySource": "Water",
                    "energySourcePriceExVat": {
                        "value": 1.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "energySource": "Wind",
                    "energySourcePriceExVat": {
                        "value": 2.52,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "_premiseId": "1"
                },
                {
                    "energySource": "Nuclear",
                    "energySourcePriceExVat": {
                        "value": -1.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "_premiseId": "1"
                },
                {
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 4.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "_premiseId": "1"
                }
            ]
        },
        "/elements/flows-energy-source-select-acquisition-section": {
            "GET": [
                {
                    "_callerId": "1",
                    "_customerType": "SME",
                    "energySource": "Water",
                    "energySourcePriceExVat": {
                        "value": 1.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "_callerId": "1",
                    "_customerType": "SME",
                    "energySource": "Wind",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "_callerId": "1",
                    "_customerType": "SME",
                    "energySource": "Nuclear",
                    "energySourcePriceExVat": {
                        "value": 2.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "_callerId": "1",
                    "_customerType": "SME",
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 2.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                }
            ]
        },
        "/elements/flows-energy-source-select-change-section": {
            "GET": [
                {
                    "_premiseId": "TUM735999100054769321",
                    "_callerId": "MS.SE4.0",
                    "energySource": "Water",
                    "energySourcePriceExVat": {
                        "value": 1.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "_premiseId": "TUM735999100054769321",
                    "_callerId": "MS.SE4.0",
                    "energySource": "Wind",
                    "energySourcePriceExVat": {
                        "value": 4.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "_premiseId": "TUM735999100054769321",
                    "_callerId": "MS.SE4.0",
                    "energySource": "Nuclear",
                    "energySourcePriceExVat": {
                        "value": 2.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                },
                {
                    "_premiseId": "TUM735999100054769321",
                    "_callerId": "MS.SE4.0",
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 3.0,
                        "unit": "SWEDISH_ORE/KWH"
                    }
                }
            ]
        },
        "/elements/flows-contract-details-section/premises/:premiseId": {
            "GET": [
                {
                    "_premiseId": "UPP1234567890",
                    "estimatedYearlyConsumption": {
                        "value": 24773,
                        "unit": "KWH"
                    },
                    "priceValidStartDate": 1644192000.0
                },
                {
                    "_premiseId": "TUM735999100054769321",
                    "estimatedYearlyConsumption": {
                        "value": 24773,
                        "unit": "KWH"
                    },
                    "priceValidStartDate": 1644192000.0,
                    "fixedPriceValidStartDate": 1680300000.0,
                    "winterInsuranceValidStartDate": 1696170011.0
                }
            ]
        },
        "/elements/flows-product-select-option/acquisition/products/:productId": {
            "GET": [
                {
                    "_productId": "CompanyFixed3Years",
                    "campaignDiscountDuration": {
                        "unit": "MONTHS",
                        "value": 12
                    },
                    "campaignDiscountExVat": {
                        "unit": "SWEDISH_KRONA/MONTH",
                        "value": 36
                    },
                    "campaignDiscountTotalExVat": {
                        "unit": "SWEDISH_KRONA",
                        "value": 960
                    },
                    "electricityPriceTotalExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "rightToChangePriceTotalExVat": {
                        "value": 200.0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceExVat": {
                        "value": 295.0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceDiscountExVat": {
                        "value": 95.0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.43, "unit": "SWEDISH_KRONA/MONTH" },
                    "productId": "CompanyFixed3Years",
                    "productName": "Rådgivningsavtal med fast elpris 3 år (har rtc rabatt)",
                    "isRecommended": true
                },
                {
                    "_productId": "CompanyFixed2Years",
                    "electricityPriceTotalExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "rightToChangePriceTotalExVat": { "value": 195.0, "unit": "SWEDISH_KRONA" },
                    "rightToChangePriceExVat": { "value": 195.0, "unit": "SWEDISH_KRONA" },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.43, "unit": "SWEDISH_KRONA/MONTH" },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "productId": "CompanyFixed2Years",
                    "productName": "Rådgivningsavtal med fast elpris 2 år (ej rtc rabatt)",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyFixed6Months",
                    "electricityPriceTotalExVat": { "value": 37.8, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 37.8, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.44, "unit": "SWEDISH_KRONA/MONTH" },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "productName": "Rådgivningsavtal med fast elpris 6 månader",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyFixed1Years",
                    "electricityPriceTotalExVat": { "value": 36.5, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 36.5, "unit": "SWEDISH_ORE/KWH" },
                    "rightToChangePriceTotalExVat": { "value": 125.0, "unit": "SWEDISH_KRONA" },
                    "rightToChangePriceExVat": { "value": 125.0, "unit": "SWEDISH_KRONA" },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.43, "unit": "SWEDISH_KRONA/MONTH" },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "productName": "Rådgivningsavtal med fast elpris 1 år",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyVariableRate",
                    "electricityPriceTotalExVat": { "value": 36.15, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 29.98, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceIsValidForMonth": 10,
                    "electricityPriceSurchargeExVat": { "value": 4.0, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountExVat": { "value": 4, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountDuration": { "value": 6, "unit": "MONTHS" },
                    "electricityCertificateExVat": { "value": 2.17, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 36.42, "unit": "SWEDISH_KRONA/MONTH" },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "productName": "Rådgivningsavtal med rörligt elpris",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyVariableRateHourly",
                    "electricityPriceTotalExVat": { "value": 36.15, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 29.98, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceIsValidForMonth": 10,
                    "electricityPriceSurchargeExVat": { "value": 4.0, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountExVat": { "value": 4, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountDuration": { "value": 6, "unit": "MONTHS" },
                    "electricityCertificateExVat": { "value": 2.17, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 36.42, "unit": "SWEDISH_KRONA/MONTH" },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "productName": "Rådgivningsavtal med rörligt elpris hourly",
                    "isRecommended": false
                },
                {
                    "_productId": "Failed",
                    "electricityPriceTotalExVat": { "value": 36.15, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 29.98, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceIsValidForMonth": 10,
                    "electricityPriceSurchargeExVat": { "value": 4.0, "unit": "SWEDISH_ORE/KWH" },
                    "electricityCertificateExVat": { "value": 2.17, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 36.42, "unit": "SWEDISH_KRONA/MONTH" },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "productName": "This matching product is used to develop an order that will return a mocked orderStatus FAILED in flows-submit-renewal-section",
                    "isRecommended": false
                },
                {
                    "_productId": "PrivateFixed3Years",
                    "totalPricePerkWhIncVat": {
                        "value": 117.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 102.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 24.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 123.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1468.75,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 75,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 1800,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 120.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed3Years",
                    "productName": "Fast elpris 36 mån",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateFixed2Years",
                    "totalPricePerkWhIncVat": {
                        "value": 134.98,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 112.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 27.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 135.38,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1687.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 120,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 135.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed2Years",
                    "productName": "Fast elpris 2 år",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateFixed1Years",
                    "totalPricePerkWhIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 136.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 33.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 2073.5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "comparisonPriceIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed1Years",
                    "productName": "Fast elpris 1 år",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateFixed6Months",
                    "totalPricePerkWhIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 136.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 33.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 2073.5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "comparisonPriceIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed6Months",
                    "productName": "Fast elpris 6 månader",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateVariableRate",
                    "totalPricePerkWhIncVat": {
                        "value": 120.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 121.78,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 96.02,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 24.36,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 8,
                    "electricityPriceSurchargeExVat": {
                        "value": 6,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 6,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 270,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 270,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1502.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 120.58,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateVariableRate",
                    "productName": "Rörligt elpris",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateVariableRateHourly",
                    "totalPricePerkWhIncVat": {
                        "value": 120.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 121.78,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 96.02,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 24.36,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 8,
                    "electricityPriceSurchargeExVat": {
                        "value": 6,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 6,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 270,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 270,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1502.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 120.58,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateVariableRateHourly",
                    "productName": "Rörligt elpris hourly",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateWinterInsurance",
                    "totalPricePerkWhIncVat": {
                        "value": 134.98,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 112.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 27.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 135.38,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1687.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 120,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 135.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateWinterInsurance",
                    "productType": "WinterInsuranceElectricity",
                    "productName": "Winter Insurance",
                    "sortOrder": 0
                }
            ]
        },
        "/elements/flows-move-private-sweden-checkout/invoice-settings/payment/banks": {
            "GET": [
                {
                    "bankName": "Danske Bank",
                    "bankId": "0010"
                },
                {
                    "bankName": "Handelsbanken",
                    "bankId": "0020"
                },
                {
                    "bankName": "ICA Banken",
                    "bankId": "0030"
                },
                {
                    "bankName": "Länsförsäkringar Bank",
                    "bankId": "0040"
                },
                {
                    "bankName": "Nordea (NB)",
                    "bankId": "0050"
                },
                {
                    "bankName": "SEB",
                    "bankId": "0070"
                },
                {
                    "bankName": "SkandiaBanken",
                    "bankId": "0080"
                },
                {
                    "bankName": "Sparbanken Syd",
                    "bankId": "0090"
                },
                {
                    "bankName": "Swedbank",
                    "bankId": "0110"
                },
                {
                    "bankName": "Ålandsbanken",
                    "bankId": "0120"
                },
                {
                    "bankName": "Marginalen Bank",
                    "bankId": "0140"
                }
            ]
        },
        "/elements/flows-move-private-sweden-checkout/products/:productId": {
            "GET": [
                {
                    "electricityPriceTotalIncVat": {
                        "value": 16.25,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 13.00,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 250.00,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 250.00,
                        "unit": "SWEDISH_KRONA"
                    },
                    "vat": {
                        "value": 3.25,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 480.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 202.50,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "comparisonPriceIncVat": {
                        "value": 25.85,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 5000,
                        "unit": "KWH"
                    },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "_productId": "PrivateFixed1Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 1 år",
                    "contractStartDate": 1646348400.0,
                    "contractEndDate": 1654120800.0
                },
                {
                    "electricityPriceTotalIncVat": {
                        "value": 16.25,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 13.00,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 250.00,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 250.00,
                        "unit": "SWEDISH_KRONA"
                    },
                    "vat": {
                        "value": 3.25,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 480.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 202.50,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "comparisonPriceIncVat": {
                        "value": 25.85,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 5000,
                        "unit": "KWH"
                    },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "_productId": "PrivateFixed2Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 1 år",
                    "contractStartDate": 1646348400.0,
                    "contractEndDate": 1654120800.0
                }
            ]
        },
        "/elements/flows-new-contract-details-section/deliverydates": {
            "GET": [
                {
                    "contractDeliveryStartDate": 1620086400,
                    "contractDeliveryEndDate": 1620086400,
                    "winterInsuranceValidStartDate": 1696170011.0
                }
            ]
        },
        "/elements/flows-product-select-option/renewal/products/:productId": {
            "GET": [
                {
                    "_productId": "CompanyFixed3Years",
                    "electricityPriceTotalExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "rightToChangePriceTotalExVat": {
                        "value": 200.0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceExVat": {
                        "value": 295.0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceDiscountExVat": {
                        "value": 95.0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.43, "unit": "SWEDISH_KRONA/MONTH" },
                    "productId": "CompanyFixed3Years",
                    "productType": "FixedPriceSmallEnterprise",
                    "productName": "Rådgivningsavtal med fast elpris 3 år (har rtc rabatt)",
                    "isRecommended": true
                },
                {
                    "_productId": "CompanyFixed2Years",
                    "electricityPriceTotalExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 36.6, "unit": "SWEDISH_ORE/KWH" },
                    "rightToChangePriceTotalExVat": { "value": 195.0, "unit": "SWEDISH_KRONA" },
                    "rightToChangePriceExVat": { "value": 195.0, "unit": "SWEDISH_KRONA" },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.43, "unit": "SWEDISH_KRONA/MONTH" },
                    "productId": "CompanyFixed2Years",
                    "productType": "FixedPriceSmallEnterprise",
                    "productName": "Rådgivningsavtal med fast elpris 2 år (ej rtc rabatt)",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyFixed6Months",
                    "electricityPriceTotalExVat": { "value": 37.8, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 37.8, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.44, "unit": "SWEDISH_KRONA/MONTH" },
                    "productName": "Rådgivningsavtal med fast elpris 6 månader",
                    "productType": "FixedPriceSmallEnterprise",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyFixed1Years",
                    "_premiseId": "UPP1234567890",
                    "_energySource": "Mix",
                    "electricityPriceTotalExVat": { "value": 36.5, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 36.5, "unit": "SWEDISH_ORE/KWH" },
                    "rightToChangePriceTotalExVat": { "value": 125.0, "unit": "SWEDISH_KRONA" },
                    "rightToChangePriceExVat": { "value": 125.0, "unit": "SWEDISH_KRONA" },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 384.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 32.43, "unit": "SWEDISH_KRONA/MONTH" },
                    "productName": "Rådgivningsavtal med fast elpris 1 år",
                    "productType": "FixedPriceSmallEnterprise",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyVariableRate",
                    "electricityPriceTotalExVat": { "value": 36.15, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 29.98, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceIsValidForMonth": 7,
                    "electricityPriceSurchargeExVat": { "value": 4.0, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountExVat": { "value": 4, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountDuration": { "value": 6, "unit": "MONTHS" },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateExVat": { "value": 2.17, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 36.42, "unit": "SWEDISH_KRONA/MONTH" },
                    "productName": "Rådgivningsavtal med rörligt elpris",
                    "productType": "SpotPriceSmallEnterprise",
                    "isRecommended": false
                },
                {
                    "_productId": "CompanyVariableRateHourly",
                    "electricityPriceTotalExVat": { "value": 36.15, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 29.98, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceIsValidForMonth": 7,
                    "electricityPriceSurchargeExVat": { "value": 4.0, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountExVat": { "value": 4, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceSurchargeDiscountDuration": { "value": 6, "unit": "MONTHS" },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateExVat": { "value": 2.17, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 36.42, "unit": "SWEDISH_KRONA/MONTH" },
                    "productName": "Rådgivningsavtal med rörligt elpris Hourly spot",
                    "productType": "SpotPriceHourlySmallEnterprise",
                    "isRecommended": false
                },
                {
                    "_productId": "Failed",
                    "electricityPriceTotalExVat": { "value": 36.15, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceExVat": { "value": 29.98, "unit": "SWEDISH_ORE/KWH" },
                    "electricityPriceIsValidForMonth": 10,
                    "electricityPriceSurchargeExVat": { "value": 4.0, "unit": "SWEDISH_ORE/KWH" },
                    "electricityCertificateExVat": { "value": 2.17, "unit": "SWEDISH_ORE/KWH" },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": { "value": 0.0, "unit": "SWEDISH_ORE/KWH" },
                    "fixedFeeTotalExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "fixedFeeExVat": { "value": 432.0, "unit": "SWEDISH_KRONA/YEAR" },
                    "estimatedMonthlyCostExVat": { "value": 36.42, "unit": "SWEDISH_KRONA/MONTH" },
                    "productName": "This matching product is used to develop an order that will return a mocked orderStatus FAILED in flows-submit-renewal-section",
                    "isRecommended": false
                }
            ]
        },
        "/elements/flows-acquisition-private-sweden-checkout/invoice-settings/payment/banks": {
            "GET": [
                {
                    "bankName": "Danske Bank",
                    "bankId": "0010"
                },
                {
                    "bankName": "Handelsbanken",
                    "bankId": "0020"
                },
                {
                    "bankName": "ICA Banken",
                    "bankId": "0030"
                },
                {
                    "bankName": "Länsförsäkringar Bank",
                    "bankId": "0040"
                },
                {
                    "bankName": "Nordea (NB)",
                    "bankId": "0050"
                },
                {
                    "bankName": "SEB",
                    "bankId": "0070"
                },
                {
                    "bankName": "SkandiaBanken",
                    "bankId": "0080"
                },
                {
                    "bankName": "Sparbanken Syd",
                    "bankId": "0090"
                },
                {
                    "bankName": "Swedbank",
                    "bankId": "0110"
                },
                {
                    "bankName": "Ålandsbanken",
                    "bankId": "0120"
                },
                {
                    "bankName": "Marginalen Bank",
                    "bankId": "0140"
                }
            ]
        },
        "/elements/flows-acquisition-private-sweden-checkout/products/:productId": {
            "GET": [
                {
                    "_productId": "PrivateVariableRate",
                    "campaignDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "electricityPriceExVat": {
                        "value": 53.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 1,
                    "electricityPriceSurchargeExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 1,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 36.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.83,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 958.34,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "estimatedMonthlyCostExDiscountIncVat": {
                        "value": 1000.34,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountIncVat": {
                        "value": 36,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExDiscountIncVat": { "value": 135.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyVariableRate",
                    "productType": "SpotPriceElectricity",
                    "productName": "Rörligt elpris",
                    "contractStartDate": 1620086400,
                    "contractEndDate": 1641600000
                },
                {
                    "_productId": "PrivateVariableRateHourly",
                    "campaignDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "electricityPriceExVat": {
                        "value": 53.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 1,
                    "electricityPriceSurchargeExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 1,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 36.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.83,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 958.34,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "estimatedMonthlyCostExDiscountIncVat": {
                        "value": 1001.34,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountIncVat": {
                        "value": 36,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExDiscountIncVat": { "value": 135.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "PrivateVariableRateHourly",
                    "productType": "SpotPriceHourlyElectricity",
                    "productName": "Rörligt elpris hourly",
                    "contractStartDate": 1620086400,
                    "contractEndDate": 1641600000
                },
                {
                    "_productId": "PrivateFixed1Years",
                    "campaignDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "comparisonConsumption": {
                        "unit": "KWH",
                        "value": 5000
                    },
                    "comparisonPriceIncVat": {
                        "unit": "SWEDISH_ORE/KWH",
                        "value": 97.98
                    },
                    "electricityPriceExVat": {
                        "value": 40.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 36.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "isRightToChangeAvailable": true,
                    "rightToChangePriceIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 958.34,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "estimatedMonthlyCostExDiscountIncVat": {
                        "value": 1000.34,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountIncVat": {
                        "value": 36,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "totalPricePerkWhIncVat": { "value": 131.35, "unit": "SWEDISH_ORE/KWH" },
                    "totalPricePerkWhExDiscountIncVat": { "value": 135.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "PrivateFixed1Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 3 år",
                    "contractStartDate": 1628899200,
                    "contractEndDate": 1628899200
                },
                {
                    "_productId": "PrivateFixed3Years",
                    "electricityPriceTotalIncVat": {
                        "value": 123.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 102.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "vat": {
                        "value": 24.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1398.77,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "estimatedMonthlyCostExDiscountIncVat": {
                        "value": 1400.77,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountIncVat": {
                        "value": 75,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 120.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "totalPricePerkWhIncVat": {
                        "value": 117.22,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "totalPricePerkWhExDiscountIncVat": {
                        "value": 120.22,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "hasMonthlyDiscounts": true,
                    "productId": "PrivateFixed3Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 3 år",
                    "contractStartDate": 1661810400,
                    "contractEndDate": 1672959600
                }
            ]
        },
        "/elements/flows-acquisition-business-sweden-checkout/products/:productId": {
            "GET": [
                {
                    "_productId": "CompanyVariableRate",
                    "campaignDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "campaignDiscountExVat": {
                        "value": 36,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "electricityPriceTotalExVat": {
                        "value": 58.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 53.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 1,
                    "electricityPriceSurchargeExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 1,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.83,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostExVat": {
                        "value": 598.53,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyVariableRate",
                    "productType": "SpotPriceSmallEnterprise",
                    "productName": "Rörligt elpris",
                    "contractStartDate": 1620086400,
                    "contractEndDate": 1641600000
                },
                {
                    "_productId": "CompanyVariableRateHourly",
                    "campaignDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "campaignDiscountExVat": {
                        "value": 36,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "electricityPriceTotalExVat": {
                        "value": 58.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 53.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 1,
                    "electricityPriceSurchargeExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 1,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.83,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostExVat": {
                        "value": 598.53,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyVariableRateHourly",
                    "productType": "SpotPriceHourlySmallEnterprise",
                    "productName": "Rörligt elpris",
                    "contractStartDate": 1620086400,
                    "contractEndDate": 1641600000
                },
                {
                    "_productId": "CompanyFixed3Years",
                    "campaignDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "campaignDiscountExVat": {
                        "value": 36,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "electricityPriceTotalExVat": {
                        "value": 36.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 40.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceExVat": {
                        "value": 745,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceDiscountExVat": {
                        "value": 745,
                        "unit": "SWEDISH_KRONA"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 384,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 384,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostExVat": {
                        "value": 378.58,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyFixed3Years",
                    "productType": "FixedPriceSmallEnterprise",
                    "productName": "Fast elpris 3 år",
                    "contractStartDate": 1628899200,
                    "contractEndDate": 1628899200
                }
            ]
        },
        "/elements/flows-move-business-sweden-checkout/products/:productId": {
            "GET": [
                {
                    "_productId": "CompanyVariableRate",
                    "electricityPriceTotalExVat": {
                        "value": 58.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 53.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 1,
                    "electricityPriceSurchargeExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 1,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.83,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostExVat": {
                        "value": 598.53,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyVariableRate",
                    "productType": "SpotPriceSmallEnterprise",
                    "productName": "Rörligt elpris",
                    "contractStartDate": 1620086400,
                    "contractEndDate": 1641600000
                },
                {
                    "_productId": "CompanyVariableRateHourly",
                    "electricityPriceTotalExVat": {
                        "value": 58.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 53.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 1,
                    "electricityPriceSurchargeExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountExVat": {
                        "value": 1,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceSurchargeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.83,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 432,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostExVat": {
                        "value": 598.53,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyVariableRateHourly",
                    "productType": "SpotPriceHourlySmallEnterprise",
                    "productName": "Rörligt elpris hourly",
                    "contractStartDate": 1620086400,
                    "contractEndDate": 1641600000
                },
                {
                    "_productId": "CompanyFixed3Years",
                    "electricityPriceTotalExVat": {
                        "value": 36.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 40.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceExVat": {
                        "value": 745,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceDiscountExVat": {
                        "value": 745,
                        "unit": "SWEDISH_KRONA"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "isRightToChangeAvailable": true,
                    "energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 384,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 384,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostExVat": {
                        "value": 378.58,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "totalPricePerkWhExVat": { "value": 121.35, "unit": "SWEDISH_ORE/KWH" },
                    "hasMonthlyDiscounts": true,
                    "productId": "CompanyFixed3Years",
                    "productType": "FixedPriceSmallEnterprise",
                    "productName": "Fast elpris 3 år",
                    "contractStartDate": 1628899200,
                    "contractEndDate": 1628899200
                }
            ]
        },
        "/elements/flows-premises-select/change": {
            "GET": [
                {
                    "id": "UPN735999100001143334",
                    "name": "ChangeEvenemangsgatan 1",
                    "postalCode": "168 53",
                    "postalTown": "Bromma",
                    "premiseCampaignRemainingDays": {
                        "value": 0,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614347869
                },
                {
                    "id": "UMA735999101100453744",
                    "name": "Arenavägen 1",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA",
                    "premiseCampaignRemainingDays": {
                        "value": 1,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614434269
                },
                {
                    "id": "EKG735999114010270776",
                    "name": "Evenemangsgatan 12",
                    "postalCode": "168 53",
                    "postalTown": "Bromma",
                    "premiseCampaignRemainingDays": {
                        "value": 2,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614520669
                },
                {
                    "id": "DAD735999100053227983",
                    "name": "Arenavägen 1",
                    "postalCode": "213 45",
                    "postalTown": "New York",
                    "premiseCampaignRemainingDays": {
                        "value": 3,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614607069
                },
                {
                    "id": "FLE735999100054145255",
                    "name": "Arenavägen 14",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA",
                    "premiseCampaignRemainingDays": {
                        "value": 4,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614693469
                },
                {
                    "id": "MAI735999182001132402",
                    "name": "Sven Botvids Allé 24",
                    "postalCode": "66 68",
                    "postalTown": "DO",
                    "premiseCampaignRemainingDays": {
                        "value": 5,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614779869
                },
                {
                    "id": "SHD735999100000096365",
                    "name": "Evenemangsgatan 1",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA",
                    "premiseCampaignRemainingDays": {
                        "value": 6,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1644192000.0
                }
            ]
        },
        "/elements/flows-premises-select/renewal": {
            "GET": [
                {
                    "id": "UPN735999100001143334",
                    "name": "Evenemangsgatan 1",
                    "postalCode": "168 53",
                    "postalTown": "Bromma",
                    "premiseCampaignRemainingDays": {
                        "value": 0,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614347869
                },
                {
                    "id": "UMA735999101100453744",
                    "name": "Arenavägen 1",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA",
                    "premiseCampaignRemainingDays": {
                        "value": 1,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614434269
                },
                {
                    "id": "EKG735999114010270776",
                    "name": "Evenemangsgatan 12",
                    "postalCode": "168 53",
                    "postalTown": "Bromma",
                    "premiseCampaignRemainingDays": {
                        "value": 2,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614520669
                },
                {
                    "id": "DAD735999100053227983",
                    "name": "Arenavägen 1",
                    "postalCode": "213 45",
                    "postalTown": "New York",
                    "premiseCampaignRemainingDays": {
                        "value": 3,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614607069
                },
                {
                    "id": "FLE735999100054145255",
                    "name": "Arenavägen 14",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA",
                    "premiseCampaignRemainingDays": {
                        "value": 4,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614693469
                },
                {
                    "id": "MAI735999182001132402",
                    "name": "Sven Botvids Allé 24",
                    "postalCode": "66 68",
                    "postalTown": "DO",
                    "premiseCampaignRemainingDays": {
                        "value": 5,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1614779869
                },
                {
                    "id": "SHD735999100000096365",
                    "name": "Evenemangsgatan 1",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA",
                    "premiseCampaignRemainingDays": {
                        "value": 6,
                        "unit": "DAYS_REMAINING"
                    },
                    "premiseCampaignToDate": 1644192000.0
                }
            ]
        },
        "/elements/flows-premises-select/winter-assurance": {
            "GET": [
                {
                    "id": "UPN735999100001143334",
                    "name": "Evenemangsgatan 1",
                    "postalCode": "168 53",
                    "postalTown": "Bromma"
                },
                {
                    "id": "UMA735999101100453744",
                    "name": "Arenavägen 1",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA"
                },
                {
                    "id": "EKG735999114010270776",
                    "name": "Evenemangsgatan 12",
                    "postalCode": "168 53",
                    "postalTown": "Bromma"
                },
                {
                    "id": "DAD735999100053227983",
                    "name": "Arenavägen 1",
                    "postalCode": "213 45",
                    "postalTown": "New York"
                },
                {
                    "id": "FLE735999100054145255",
                    "name": "Arenavägen 14",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA"
                },
                {
                    "id": "MAI735999182001132402",
                    "name": "Sven Botvids Allé 24",
                    "postalCode": "66 68",
                    "postalTown": "DO"
                },
                {
                    "id": "SHD735999100000096365",
                    "name": "Evenemangsgatan 1",
                    "postalCode": "756 54",
                    "postalTown": "UPPSALA"
                }
            ]
        },
        "/elements/flows-premise-contract-summary/premises/:premiseId": {
            "GET": [
                {
                    "_premiseId": "UPP1234567890",
                    "streetName": "Äggelundavägen 2 A",
                    "postalCode": "123 45",
                    "postalTown": "Växjö",
                    "contractName": "Fast elpris 3 år",
                    "contractStartDate": 1577836800.0,
                    "contractEndDate": 1609372800.0,
                    "electricityPriceTotalExVat": {
                        "value": 64.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 336.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "invoicesPerYear": {
                        "value": 12,
                        "unit": "INVOICES_PER_YEAR"
                    }
                },
                {
                    "_premiseId": "TUM735999100054769321",
                    "electricityPriceStartDate": 1669852800.0,
                    "electricityPriceTotalIncVat": {
                        "value": 371.46,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "premiseId": "TUM735999100054769321",
                    "streetName": "Postängsvägen 178",
                    "postalCode": "145 52",
                    "postalTown": "NORSBORG",
                    "contractName": "Rörligt elpris",
                    "contractStartDate": 1656633600.0,
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    }
                },
                {
                    "_premiseId": "SHD735999100000075025",
                    "electricityPriceTotalIncVat": {
                        "value": 221.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "premiseId": "SHD735999100000075025",
                    "streetName": "Odens Väg 20 H",
                    "postalCode": "517 36",
                    "postalTown": "BOLLEBYGD",
                    "contractName": "Anvisningsavtal",
                    "contractStartDate": 1661983200.0,
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    }
                },
                {
                    "_premiseId": "TNN735999100056567062",
                    "streetName": "Hap Hamn",
                    "postalCode": "953 95",
                    "postalTown": "NIKKALA",
                    "contractName": "Timpris",
                    "contractStartDate": 1592344800.0,
                    "invoicesPerYear": {
                        "value": 12,
                        "unit": "INVOICES_PER_YEAR"
                    }
                }
            ]
        },
        "/elements/my-contact-notification-dialog/customer/contact-information": {
            "GET": [
                {
                    "mobileNumber": "0765432",
                    "emailAddress": "asdf@ads.com",
                    "displayNotification": true
                }
            ],
            "PUT": [
                {
                    "mobileNumber": "0765432",
                    "emailAddress": "asdf@ads.com"
                }
            ]
        },
        "/elements/flows-acquisition-business-sweden-checkout/orders": {
            "POST": [
                {
                    "orderId": "VAT28",
                    "orderStatus": "SUCCESS"
                }
            ]
        },
        "/elements/flows-acquisition-private-sweden-checkout/orders": {
            "POST": [
                {
                    "orderId": "VAT28",
                    "orderStatus": "SUCCESS"
                }
            ]
        },
        "/elements/flows-move-business-sweden-checkout/orders": {
            "POST": [
                {
                    "orderId": "VAT28",
                    "orderStatus": "SUCCESS"
                }
            ]
        },
        "/elements/postal-code-grid-area/grid-areas/:gridArea": {
            "GET": [
                {
                    "_gridArea": "maf",
                    "postalTown": "",
                    "gridArea": "MAF",
                    "energyArea": "SN1"
                },
                {
                    "_gridArea": "all",
                    "postalTown": "",
                    "gridArea": "ALL",
                    "energyArea": "SN2"
                },
                {
                    "_gridArea": "sth",
                    "postalTown": "",
                    "gridArea": "STH",
                    "energyArea": "SN3"
                },
                {
                    "_gridArea": "lun",
                    "postalTown": "",
                    "gridArea": "LUN",
                    "energyArea": "SN4"
                }
            ]
        },
        "/elements/postal-code-grid-area/postal-codes/:postalCode": {
            "GET": [
                {
                    "_postalCode": "31190",
                    "postalTown": "MORUP",
                    "gridArea": "",
                    "energyArea": "SN4"
                },
                {
                    "_postalCode": "98238",
                    "postalTown": "GÄLLIVARE",
                    "gridArea": "MAF",
                    "energyArea": "SN1"
                },
                {
                    "_postalCode": "83335",
                    "postalTown": "STRÖMSUND",
                    "gridArea": "ALL",
                    "energyArea": "SN2"
                },
                {
                    "_postalCode": "12345",
                    "postalTown": "FARSTA",
                    "gridArea": "STH",
                    "energyArea": "SN3"
                },
                {
                    "_postalCode": "22222",
                    "postalTown": "LUND",
                    "gridArea": "LUN",
                    "energyArea": "SN4"
                },
                {
                    "_postalCode": "99988",
                    "postalTown": "",
                    "gridArea": "STH",
                    "energyArea": "SN3"
                },
                {
                    "_postalCode": "10111",
                    "postalTown": "NOENERGYAREATOWN",
                    "gridArea": "MMO",
                    "energyArea": ""
                }
            ]
        },
        "/elements/flows-move-acquisition-private-switch/estimated-consumption/building-types/:buildingType": {
            "GET": [
                {
                    "_buildingType": "villa",
                    "estimatedConsumptions": [
                        {
                            "livingArea": 25,
                            "estimatedConsumption": {
                                "value": 6150,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 40,
                            "estimatedConsumption": {
                                "value": 7440,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 50,
                            "estimatedConsumption": {
                                "value": 8300,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 60,
                            "estimatedConsumption": {
                                "value": 9160,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 70,
                            "estimatedConsumption": {
                                "value": 10020,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 80,
                            "estimatedConsumption": {
                                "value": 10880,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 90,
                            "estimatedConsumption": {
                                "value": 11740,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 100,
                            "estimatedConsumption": {
                                "value": 12600,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 110,
                            "estimatedConsumption": {
                                "value": 13460,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 120,
                            "estimatedConsumption": {
                                "value": 14320,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 130,
                            "estimatedConsumption": {
                                "value": 15180,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 140,
                            "estimatedConsumption": {
                                "value": 16040,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 150,
                            "estimatedConsumption": {
                                "value": 16900,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 160,
                            "estimatedConsumption": {
                                "value": 17760,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 170,
                            "estimatedConsumption": {
                                "value": 18620,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 180,
                            "estimatedConsumption": {
                                "value": 19480,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 190,
                            "estimatedConsumption": {
                                "value": 20340,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 200,
                            "estimatedConsumption": {
                                "value": 21200,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 215,
                            "estimatedConsumption": {
                                "value": 22490,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 230,
                            "estimatedConsumption": {
                                "value": 23780,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 245,
                            "estimatedConsumption": {
                                "value": 25070,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 260,
                            "estimatedConsumption": {
                                "value": 26360,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 280,
                            "estimatedConsumption": {
                                "value": 28080,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 300,
                            "estimatedConsumption": {
                                "value": 29800,
                                "unit": "KWH_PER_YEAR"
                            }
                        }
                    ]
                },
                {
                    "_buildingType": "apartment",
                    "estimatedConsumptions": [
                        {
                            "livingArea": 25,
                            "estimatedConsumption": {
                                "value": 1750,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 40,
                            "estimatedConsumption": {
                                "value": 2320,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 50,
                            "estimatedConsumption": {
                                "value": 2700,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 60,
                            "estimatedConsumption": {
                                "value": 3080,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 70,
                            "estimatedConsumption": {
                                "value": 3460,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 80,
                            "estimatedConsumption": {
                                "value": 3840,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 90,
                            "estimatedConsumption": {
                                "value": 4220,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 100,
                            "estimatedConsumption": {
                                "value": 4600,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 110,
                            "estimatedConsumption": {
                                "value": 4980,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 120,
                            "estimatedConsumption": {
                                "value": 5360,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 130,
                            "estimatedConsumption": {
                                "value": 5740,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 140,
                            "estimatedConsumption": {
                                "value": 6120,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 150,
                            "estimatedConsumption": {
                                "value": 6500,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 160,
                            "estimatedConsumption": {
                                "value": 6880,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 170,
                            "estimatedConsumption": {
                                "value": 7260,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 180,
                            "estimatedConsumption": {
                                "value": 7640,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 190,
                            "estimatedConsumption": {
                                "value": 8020,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 200,
                            "estimatedConsumption": {
                                "value": 8400,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 215,
                            "estimatedConsumption": {
                                "value": 8970,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 230,
                            "estimatedConsumption": {
                                "value": 9540,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 245,
                            "estimatedConsumption": {
                                "value": 10110,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 260,
                            "estimatedConsumption": {
                                "value": 10680,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 280,
                            "estimatedConsumption": {
                                "value": 11440,
                                "unit": "KWH_PER_YEAR"
                            }
                        },
                        {
                            "livingArea": 300,
                            "estimatedConsumption": {
                                "value": 12200,
                                "unit": "KWH_PER_YEAR"
                            }
                        }
                    ]
                }
            ]
        },
        "/elements/my-profile-details-view/customer/profile-information": {
            "GET": [
                {
                    "customerNumber": "ABC1234567890",
                    "phoneNumber": "087654321",
                    "firstName": "Adam",
                    "lastName": "Bertilsson",
                    "streetName": "Äggelundavägen 2 A",
                    "postalCode": "123 45",
                    "postalTown": "Växjö"
                }
            ],
            "PATCH": [
                {
                    "phoneNumber": "0707654321"
                }
            ]
        },
        "/elements/my-contracts-view/premises/:premiseId": {
            "GET": [
                {
                    "$array": true,
                    "_premiseId": "1",
                    "contractState": "Future",
                    "electricityPriceExVat": {
                        "value": 82.9,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 20.73,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 103.63,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "fixedFeeTotalIncVat": {
                        "value": 440.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 100,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "HUV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 1942.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1484870400.0,
                    "contractName": "Anvisningsavtal",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TariffPrice",
                    "premiseSiteName": "Hamngatan 22 D  1302",
                    "premiseId": "HUV735999100015954186",
                    "isRenewable": true,
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "1",
                    "contractState": "Active",
                    "electricityPriceExVat": {
                        "value": 82.9,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 20.73,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 103.63,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "fixedFeeTotalIncVat": {
                        "value": 440.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 100,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "HUV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 1942.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1484870400.0,
                    "contractName": "Anvisningsavtal",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TariffPrice",
                    "premiseSiteName": "Hamngatan 22 D  1302",
                    "premiseId": "HUV735999100015954186",
                    "isRenewable": true,
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "2",
                    "contractState": "Active",
                    "electricityPriceExVat": {
                        "value": 82.9,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 20.73,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 103.63,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "fixedFeeTotalIncVat": {
                        "value": 440.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 100,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "HUV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 1942.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1484870400.0,
                    "contractName": "Anvisningsavtal",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TariffPrice",
                    "premiseSiteName": "Hamngatan 22 D  1302",
                    "premiseId": "HUV735999100015954186",
                    "isRenewable": true,
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "3",
                    "contractState": "Active",
                    "electricityPriceExVat": {
                        "value": 82.9,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 20.73,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 103.63,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "fixedFeeTotalIncVat": {
                        "value": 440.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 100,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "HUV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 1942.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1484870400.0,
                    "contractName": "Anvisningsavtal",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TariffPrice",
                    "premiseSiteName": "Hamngatan 22 D  1302",
                    "premiseId": "HUV735999100015954186",
                    "isRenewable": true,
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "TARIFFWITHFUTURE",
                    "contractState": "Future",
                    "electricityPriceExVat": {
                        "value": 82.9,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 20.73,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 103.63,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "fixedFeeTotalIncVat": {
                        "value": 440.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 100,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "HUV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 1942.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1484870400.0,
                    "contractName": "Anvisningsavtal",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TariffPrice",
                    "premiseSiteName": "Hamngatan 22 D  1302",
                    "premiseId": "HUV735999100015954186",
                    "isRenewable": true,
                    "isChangeable": false
                },
                {
                    "$array": true,
                    "_premiseId": "TARIFFWITHFUTURE",
                    "electricityPriceExVat": {
                        "value": 82.9,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 20.73,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 103.63,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "fixedFeeTotalIncVat": {
                        "value": 440.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 100,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "HUV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 1942.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1484870400.0,
                    "contractName": "Anvisningsavtal",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TariffPrice",
                    "premiseSiteName": "Hamngatan 22 D  1302 copy",
                    "premiseId": "HUV735999100015954186",
                    "isRenewable": true,
                    "isChangeable": false
                },
                {
                    "$array": true,
                    "_premiseId": "FIXEDSE3Y",
                    "electricityPriceExVat": {
                        "value": 43.2,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 2.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalExVat": {
                        "value": 41.2,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1530403200,
                    "electricityPriceDiscountEndDate": 1625011200,
                    "fixedFeeTotalExVat": {
                        "value": 240.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeExVat": {
                        "value": 240.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "customersOwnReference": "",
                    "isRenewable": true,
                    "gridOwner": "E.ON Energidistribution AB",
                    "gridArea": "OGO",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 15296.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1530403200.0,
                    "contractEndDate": 1625011200.0,
                    "contractName": "Fast elpris 3 år",
                    "invoicesPerYear": {
                        "value": 4,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "FixedPriceSmallEnterprise",
                    "premiseSiteName": "Drothemsgatan",
                    "premiseId": "OGO735999114000009119",
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "FIXEDSE1Y",
                    "electricityPriceExVat": {
                        "value": 64.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalExVat": {
                        "value": 65.82,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalExVat": {
                        "value": 336.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeDiscountExVat": {
                        "value": 3.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountStartDate": 1484870400,
                    "electricityPriceDiscountEndDate": 1485870400,
                    "gridOwner": "E.ON Elnät Stockholm AB",
                    "gridArea": "UPS",
                    "energyArea": "SE3",
                    "yearlyConsumption": {
                        "value": 34427.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Wind",
                    "energySourcePriceExVat": {
                        "value": 1.52,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "contractStartDate": 1554076800,
                    "contractEndDate": 1585612800,
                    "contractName": "Fast elpris 1 år",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "FixedPriceSmallEnterprise",
                    "premiseSiteName": "Äggelundavägen 2 A",
                    "premiseId": "735999114009896437",
                    "customersOwnReference": "abcd abcd abcd abcd abcde",
                    "isRenewable": true,
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "HEAT",
                    "tariff": "Värme - Villa",
                    "energyPriceTotalIncVat": {
                        "value": 834.0,
                        "unit": "SWEDISH_KRONA/MWH"
                    },
                    "fixedFeeIncVat": {
                        "value": 4047.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "warmWaterCostIncVat": {
                        "value": 47.47,
                        "unit": "SWEDISH_KRONA/CUBIC_METER"
                    },
                    "contractStartDate": 1577836800.0,
                    "contractEndDate": 1609372800.0,
                    "contractName": "Fjärrvärme",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "Heat",
                    "premiseSiteName": "Gitarrvägen 23",
                    "premiseId": "HUPP3013196",
                    "isChangeable": true
                },
                {
                    "$array": true,
                    "_premiseId": "SPOT",
                    "electricityPriceSurchargeExVat": {
                        "value": 4.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateCostExVat": {
                        "value": 3.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 540.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "contractStartDate": 1350432000,
                    "contractName": "Rörligt elpris",
                    "gridOwner": "E.ON Elnät AB",
                    "energyArea": "SN3",
                    "invoicesPerYear": {
                        "value": 12,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "yearlyConsumption": {
                        "value": 20445.0,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "contractType": "SpotPrice",
                    "energySource": "Wind",
                    "premiseSiteName": "Dragontorpsvägen 7",
                    "premiseId": "AKB735999114009345270"
                },
                {
                    "$array": true,
                    "_premiseId": "TIMETARIFF",
                    "lowElectricityPriceExVat": {
                        "value": 41.4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "lowElectricityPriceVat": {
                        "value": 10.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "lowElectricityPriceTotalIncVat": {
                        "value": 51.75,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "highElectricityPriceExVat": {
                        "value": 41.4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "highElectricityPriceVat": {
                        "value": 10.35,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "highElectricityPriceTotalIncVat": {
                        "value": 51.75,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeIncVat": {
                        "value": 420.0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "DAD",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 6925.0,
                        "unit": "KWH"
                    },
                    "rightToChange": true,
                    "energySource": "Mixed",
                    "contractStartDate": 1525824000.0,
                    "contractEndDate": 1620432000.0,
                    "contractName": "Tidstariff",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "TimeTariffPrice",
                    "premiseSiteName": "Björkhaget 5",
                    "premiseId": "DAD735999100053445271"
                },
                {
                    "$array": true,
                    "_premiseId": "UNKNOWN",
                    "contractStartDate": 1227484800,
                    "contractName": "Rörligt elpris",
                    "invoicesPerYear": {
                        "value": 6,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "UnknownElectricityContract",
                    "premiseSiteName": "Gitarrvägen 23",
                    "premiseId": "UPP735999100057049772"
                },
                {
                    "$array": true,
                    "_premiseId": "WINTERINSURANCE",
                    "electricityPriceExVat": {
                        "value": 67,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 16.75,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 83.75,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 540,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "gridOwner": "Vattenfall Eldistribution AB",
                    "gridArea": "DRV",
                    "energyArea": "SN3",
                    "yearlyConsumption": {
                        "value": 18948,
                        "unit": "KWH"
                    },
                    "rightToChange": false,
                    "energySource": "Mixed",
                    "contractStartDate": 1577836800,
                    "contractName": "Vintersäkring",
                    "invoicesPerYear": {
                        "value": 12,
                        "unit": "INVOICES_PER_YEAR"
                    },
                    "contractType": "WinterInsurance",
                    "premiseSiteName": "Höglidsvägen 3 B",
                    "premiseId": "DRV735999100057606173"
                }
            ]
        },
        "/elements/flows-product-addons-section/customer/contact-information": {
            "GET": [
                {
                    "phoneNumber": "",
                    "emailAddress": ""
                }
            ]
        },
        "/elements/flows-product-select-option/change/products/:productId": {
            "GET": [
                {
                    "_productId": "PrivateFixed3Years",
                    "_callerId": "MS.SE4.0",
                    "_premiseId": "TUM735999100054769321",
                    "totalPricePerkWhIncVat": {
                        "value": 117.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 102.8,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 24.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 123.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 750,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 36,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1468.75,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 75,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 1800,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 120.5,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed3Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 36 mån",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateFixed2Years",
                    "_callerId": "MS.SE4.0",
                    "_premiseId": "TUM735999100054769321",
                    "totalPricePerkWhIncVat": {
                        "value": 134.98,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 112.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 27.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 135.38,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1687.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 120,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 135.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed2Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 2 år",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateFixed1Years",
                    "_callerId": "MS.SE4.0",
                    "_premiseId": "TUM735999100054769321",
                    "totalPricePerkWhIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 136.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 33.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 2073.5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 120,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 6,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed1Years",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 1 år",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateFixed6Months",
                    "_callerId": "MS.SE4.0",
                    "_premiseId": "TUM735999100054769321",
                    "_energySource": "Mix",
                    "totalPricePerkWhIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 136.7,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 33.18,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 250,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 12,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 2073.5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 90,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 3,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 165.88,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateFixed6Months",
                    "productType": "FixedPriceElectricity",
                    "productName": "Fast elpris 6 månader",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateVariableRate",
                    "_callerId": "MS.SE4.0",
                    "totalPricePerkWhIncVat": {
                        "value": 141.25,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 111.79,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 82.03,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 23.36,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 2,
                    "electricityPriceSurchargeExVat": {
                        "value": 6.00,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.40,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 545.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 545.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 0.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 326.19,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 142.79,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 2000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateVariableRate",
                    "productType": "SpotPriceElectricity",
                    "_premiseId": "TUM735999100054769321",
                    "productName": "Private Variable Rate Monthly",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateVariableRateHourly",
                    "_callerId": "MS.SE4.0",
                    "totalPricePerkWhIncVat": {
                        "value": 139.55,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 111.79,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 82.03,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 22.36,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceIsValidForMonth": 2,
                    "electricityPriceSurchargeExVat": {
                        "value": 6.00,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityCertificateExVat": {
                        "value": 1.40,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 540.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 540.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 0.00,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 226.19,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 20,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 138.79,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 2000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0.0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateVariableRateHourly",
                    "productType": "SpotPriceHourlyElectricity",
                    "_premiseId": "TUM735999100054769321",
                    "productName": "Timpris",
                    "sortOrder": 0
                },
                {
                    "_productId": "PrivateWinterInsurance",
                    "_callerId": "MS.SE4.0",
                    "_premiseId": "TUM735999100054769321",
                    "totalPricePerkWhIncVat": {
                        "value": 134.98,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 112.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 27.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 135.38,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1687.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 120,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 135.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateWinterInsurance",
                    "productType": "WinterInsuranceElectricity",
                    "productName": "Winter Insurance",
                    "sortOrder": 0,
                    "isProductHidden": false
                },
                {
                    "_productId": "PrivateWinterInsuranceHourlySpot",
                    "_callerId": "MS.SE4.0",
                    "_premiseId": "TUM735999100054769321",
                    "totalPricePerkWhIncVat": {
                        "value": 134.98,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceExVat": {
                        "value": 112.3,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceVat": {
                        "value": 27.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountExVat": {
                        "value": 4,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "electricityPriceDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "electricityPriceTotalIncVat": {
                        "value": 135.38,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "rightToChangePriceTotalIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "rightToChangePriceIncVat": {
                        "value": 375,
                        "unit": "SWEDISH_KRONA"
                    },
                    "fixedFeeTotalIncVat": {
                        "value": 0,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountIncVat": {
                        "value": 480,
                        "unit": "SWEDISH_KRONA/YEAR"
                    },
                    "fixedFeeDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "estimatedMonthlyCostIncVat": {
                        "value": 1687.25,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "hasMonthlyDiscounts": true,
                    "campaignDiscountIncVat": {
                        "value": 5,
                        "unit": "SWEDISH_KRONA/MONTH"
                    },
                    "campaignDiscountTotalIncVat": {
                        "value": 120,
                        "unit": "SWEDISH_KRONA"
                    },
                    "campaignDiscountDuration": {
                        "value": 24,
                        "unit": "MONTHS"
                    },
                    "comparisonPriceIncVat": {
                        "value": 135.08,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "comparisonConsumption": {
                        "value": 20000,
                        "unit": "KWH"
                    },
                    "isRightToChangeAvailable": false,
                    "_energySource": "Mix",
                    "energySourcePriceExVat": {
                        "value": 0,
                        "unit": "SWEDISH_ORE/KWH"
                    },
                    "productId": "PrivateWinterInsuranceHourlySpot",
                    "productType": "WinterInsuranceElectricityHourlySpot",
                    "productName": "Winter Insurance Hourly Spot",
                    "sortOrder": 0,
                    "isProductHidden": false
                }
            ]
        }
    }
};

/**
 * Sets the `ApiConfiguration` root URL from the provided base URL, or from the base URL specified in the elements environment configuration object
 */
function setBaseUrl(baseUrl) {
    const callback = (injector, apiConfiguration) => () => {
        var _a;
        // TODO (stefan): Getting the injection token from string until we have the @vattenfall/core library setup
        // After that we should provide the actual token as a dependency to this provider and get it from there
        const elementsConfiguration = injector.get("ELEMENTS_ENVIRONMENT", {});
        apiConfiguration.rootUrl =
            baseUrl || ((_a = elementsConfiguration === null || elementsConfiguration === void 0 ? void 0 : elementsConfiguration.endpoints) === null || _a === void 0 ? void 0 : _a.selfServiceApi.replace("/elements", "")) || "";
    };
    return callback;
}
/**
 * `SelfServiceModule` manages configurations used when communicating with the Self-Service API, and should be imported once at the application root.
 *
 * To create module use the `forRoot` static method, it will create a module that contain all the configurations and instantiation logic required to communicate with the Self-Service API.
 *
 * The module should be imported as follows: `imports: [ SelfServiceModule.forRoot({ baseUrl: "https://some-host.com" }) ]` in your application's root module. If no base URL has been provided, self-service module will try to read the base URL from the elements environment configuration object.
 *
 * Optionally, requests can be intercepted and return mocked data as response. To enable this feature, set the `mockup` option to `true` when calling the `forRoot` method. Mockup data needs to be added to a mockup file in the `@vattenfall/self-service` root directory. Check the package documentation for more information.
 */
class SelfServiceModule {
    constructor(parentModule, http) {
        this.parentModule = parentModule;
        this.http = http;
        if (this.parentModule) {
            throw new Error("Could not import 'SelfServiceModule'. This module has already been imported, please make sure it is imported once and in the application root module.");
        }
        if (!this.http) {
            throw new Error("Could not import 'SelfServiceModule'. You need to import the HttpClientModule in the application root module.");
        }
    }
    static forRoot(selfServiceConfig) {
        return {
            ngModule: SelfServiceModule,
            providers: [
                // Provide mockup data
                (selfServiceConfig === null || selfServiceConfig === void 0 ? void 0 : selfServiceConfig.mockup) ? {
                    provide: MOCKUP,
                    useValue: MOCKUP_DATA,
                }
                    : [],
                // Provide mockup interceptor
                (selfServiceConfig === null || selfServiceConfig === void 0 ? void 0 : selfServiceConfig.mockup) ? {
                    provide: HTTP_INTERCEPTORS,
                    useClass: SelfServiceMockupInterceptor,
                    multi: true,
                }
                    : [],
                // Provide root URL for the base service
                {
                    provide: APP_INITIALIZER,
                    useFactory: setBaseUrl(selfServiceConfig === null || selfServiceConfig === void 0 ? void 0 : selfServiceConfig.baseUrl),
                    deps: [Injector, ApiConfiguration],
                    multi: true,
                },
            ],
        };
    }
}
SelfServiceModule.ɵmod = ɵɵdefineNgModule({ type: SelfServiceModule });
SelfServiceModule.ɵinj = ɵɵdefineInjector({ factory: function SelfServiceModule_Factory(t) { return new (t || SelfServiceModule)(ɵɵinject(SelfServiceModule, 12), ɵɵinject(HttpClient, 8)); } });
/*@__PURE__*/ (function () { ɵsetClassMetadata(SelfServiceModule, [{
        type: NgModule,
        args: [{}]
    }], function () { return [{ type: SelfServiceModule, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }, { type: HttpClient, decorators: [{
                type: Optional
            }] }]; }, null); })();

/* tslint:disable */
/**
 * Base class for services
 */
class BaseService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
        this._rootUrl = '';
    }
    /**
     * Returns the root url for all operations in this service. If not set directly in this
     * service, will fallback to `ApiConfiguration.rootUrl`.
     */
    get rootUrl() {
        return this._rootUrl || this.config.rootUrl;
    }
    /**
     * Sets the root URL for API operations in this service.
     */
    set rootUrl(rootUrl) {
        this._rootUrl = rootUrl;
    }
}
BaseService.ɵfac = function BaseService_Factory(t) { return new (t || BaseService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
BaseService.ɵprov = ɵɵdefineInjectable({ token: BaseService, factory: BaseService.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(BaseService, [{
        type: Injectable
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
/**
 * Custom parameter codec to correctly handle the plus sign in parameter
 * values. See https://github.com/angular/angular/issues/18261
 */
class ParameterCodec {
    encodeKey(key) {
        return encodeURIComponent(key);
    }
    encodeValue(value) {
        return encodeURIComponent(value);
    }
    decodeKey(key) {
        return decodeURIComponent(key);
    }
    decodeValue(value) {
        return decodeURIComponent(value);
    }
}
const ParameterCodecInstance = new ParameterCodec();
/**
 * Base class for a parameter
 */
class Parameter {
    constructor(name, value, options, defaultStyle, defaultExplode) {
        this.name = name;
        this.value = value;
        this.options = options;
        this.options = options || {};
        if (this.options.style === null || this.options.style === undefined) {
            this.options.style = defaultStyle;
        }
        if (this.options.explode === null || this.options.explode === undefined) {
            this.options.explode = defaultExplode;
        }
    }
    serializeValue(value, separator = ',') {
        if (value === null || value === undefined) {
            return '';
        }
        else if (value instanceof Array) {
            return value.map(v => this.serializeValue(v).split(separator).join(encodeURIComponent(separator))).join(separator);
        }
        else if (typeof value === 'object') {
            const array = [];
            for (const key of Object.keys(value)) {
                let propVal = value[key];
                if (propVal !== null && propVal !== undefined) {
                    propVal = this.serializeValue(propVal).split(separator).join(encodeURIComponent(separator));
                    if (this.options.explode) {
                        array.push(`${key}=${propVal}`);
                    }
                    else {
                        array.push(key);
                        array.push(propVal);
                    }
                }
            }
            return array.join(separator);
        }
        else {
            return String(value);
        }
    }
}
/**
 * A parameter in the operation path
 */
class PathParameter extends Parameter {
    constructor(name, value, options) {
        super(name, value, options, 'simple', false);
    }
    append(path) {
        let value = this.value;
        if (value === null || value === undefined) {
            value = '';
        }
        let prefix = this.options.style === 'label' ? '.' : '';
        let separator = this.options.explode ? prefix === '' ? ',' : prefix : ',';
        let alreadySerialized = false;
        if (this.options.style === 'matrix') {
            // The parameter name is just used as prefix, except in some cases...
            prefix = `;${this.name}=`;
            if (this.options.explode && typeof value === 'object') {
                prefix = ';';
                if (value instanceof Array) {
                    // For arrays we have to repeat the name for each element
                    value = value.map(v => `${this.name}=${this.serializeValue(v, ';')}`);
                    value = value.join(';');
                    alreadySerialized = true;
                }
                else {
                    // For objects we have to put each the key / value pairs
                    value = this.serializeValue(value, ';');
                    alreadySerialized = true;
                }
            }
        }
        value = prefix + (alreadySerialized ? value : this.serializeValue(value, separator));
        // Replace both the plain variable and the corresponding variant taking in the prefix and explode into account
        path = path.replace(`{${this.name}}`, value);
        path = path.replace(`{${prefix}${this.name}${this.options.explode ? '*' : ''}}`, value);
        return path;
    }
    // @ts-ignore
    serializeValue(value, separator = ',') {
        var result = typeof value === 'string' ? encodeURIComponent(value) : super.serializeValue(value, separator);
        result = result.replace('%3D', '=');
        result = result.replace('%3B', ';');
        result = result.replace('%2C', ',');
        return result;
    }
}
/**
 * A parameter in the query
 */
class QueryParameter extends Parameter {
    constructor(name, value, options) {
        super(name, value, options, 'form', true);
    }
    append(params) {
        if (this.value instanceof Array) {
            // Array serialization
            if (this.options.explode) {
                for (const v of this.value) {
                    params = params.append(this.name, this.serializeValue(v));
                }
            }
            else {
                const separator = this.options.style === 'spaceDelimited'
                    ? ' ' : this.options.style === 'pipeDelimited'
                    ? '|' : ',';
                return params.append(this.name, this.serializeValue(this.value, separator));
            }
        }
        else if (this.value !== null && typeof this.value === 'object') {
            // Object serialization
            if (this.options.style === 'deepObject') {
                // Append a parameter for each key, in the form `name[key]`
                for (const key of Object.keys(this.value)) {
                    const propVal = this.value[key];
                    if (propVal !== null && propVal !== undefined) {
                        params = params.append(`${this.name}[${key}]`, this.serializeValue(propVal));
                    }
                }
            }
            else if (this.options.explode) {
                // Append a parameter for each key without using the parameter name
                for (const key of Object.keys(this.value)) {
                    const propVal = this.value[key];
                    if (propVal !== null && propVal !== undefined) {
                        params = params.append(key, this.serializeValue(propVal));
                    }
                }
            }
            else {
                // Append a single parameter whose values are a comma-separated list of key,value,key,value...
                const array = [];
                for (const key of Object.keys(this.value)) {
                    const propVal = this.value[key];
                    if (propVal !== null && propVal !== undefined) {
                        array.push(key);
                        array.push(propVal);
                    }
                }
                params = params.append(this.name, this.serializeValue(array));
            }
        }
        else if (this.value !== null && this.value !== undefined) {
            // Plain value
            params = params.append(this.name, this.serializeValue(this.value));
        }
        return params;
    }
}
/**
 * A parameter in the HTTP request header
 */
class HeaderParameter extends Parameter {
    constructor(name, value, options) {
        super(name, value, options, 'simple', false);
    }
    append(headers) {
        if (this.value !== null && this.value !== undefined) {
            if (this.value instanceof Array) {
                for (const v of this.value) {
                    headers = headers.append(this.name, this.serializeValue(v));
                }
            }
            else {
                headers = headers.append(this.name, this.serializeValue(this.value));
            }
        }
        return headers;
    }
}
/**
 * Helper to build http requests from parameters
 */
class RequestBuilder {
    constructor(rootUrl, operationPath, method) {
        this.rootUrl = rootUrl;
        this.operationPath = operationPath;
        this.method = method;
        this._path = new Map();
        this._query = new Map();
        this._header = new Map();
    }
    /**
     * Sets a path parameter
     */
    path(name, value, options) {
        this._path.set(name, new PathParameter(name, value, options || {}));
    }
    /**
     * Sets a query parameter
     */
    query(name, value, options) {
        this._query.set(name, new QueryParameter(name, value, options || {}));
    }
    /**
     * Sets a header parameter
     */
    header(name, value, options) {
        this._header.set(name, new HeaderParameter(name, value, options || {}));
    }
    /**
     * Sets the body content, along with the content type
     */
    body(value, contentType = 'application/json') {
        if (value instanceof Blob) {
            this._bodyContentType = value.type;
        }
        else {
            this._bodyContentType = contentType;
        }
        if (this._bodyContentType === 'application/x-www-form-urlencoded' && value !== null && typeof value === 'object') {
            // Handle URL-encoded data
            const pairs = [];
            for (const key of Object.keys(value)) {
                let val = value[key];
                if (!(val instanceof Array)) {
                    val = [val];
                }
                for (const v of val) {
                    const formValue = this.formDataValue(v);
                    if (formValue !== null) {
                        pairs.push([key, formValue]);
                    }
                }
            }
            this._bodyContent = pairs.map(p => `${encodeURIComponent(p[0])}=${encodeURIComponent(p[1])}`).join('&');
        }
        else if (this._bodyContentType === 'multipart/form-data') {
            // Handle multipart form data
            const formData = new FormData();
            if (value !== null && value !== undefined) {
                for (const key of Object.keys(value)) {
                    const val = value[key];
                    if (val instanceof Array) {
                        for (const v of val) {
                            const toAppend = this.formDataValue(v);
                            if (toAppend !== null) {
                                formData.append(key, toAppend);
                            }
                        }
                    }
                    else {
                        const toAppend = this.formDataValue(val);
                        if (toAppend !== null) {
                            formData.set(key, toAppend);
                        }
                    }
                }
            }
            this._bodyContent = formData;
        }
        else {
            // The body is the plain content
            this._bodyContent = value;
        }
    }
    formDataValue(value) {
        if (value === null || value === undefined) {
            return null;
        }
        if (value instanceof Blob) {
            return value;
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        return String(value);
    }
    /**
     * Builds the request with the current set parameters
     */
    build(options) {
        options = options || {};
        // Path parameters
        let path = this.operationPath;
        for (const pathParam of this._path.values()) {
            path = pathParam.append(path);
        }
        const url = this.rootUrl + path;
        // Query parameters
        let httpParams = new HttpParams({
            encoder: ParameterCodecInstance
        });
        for (const queryParam of this._query.values()) {
            httpParams = queryParam.append(httpParams);
        }
        // Header parameters
        let httpHeaders = new HttpHeaders();
        if (options.accept) {
            httpHeaders = httpHeaders.append('Accept', options.accept);
        }
        for (const headerParam of this._header.values()) {
            httpHeaders = headerParam.append(httpHeaders);
        }
        // Request content headers
        if (this._bodyContentType && !(this._bodyContent instanceof FormData)) {
            httpHeaders = httpHeaders.set('Content-Type', this._bodyContentType);
        }
        // Perform the request
        return new HttpRequest(this.method.toUpperCase(), url, this._bodyContent, {
            params: httpParams,
            headers: httpHeaders,
            responseType: options.responseType,
            reportProgress: options.reportProgress
        });
    }
}

/* tslint:disable */
class AcquisitionFlowsEnergySourcesSelectElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get a list of available energy sources with additional cost related to each energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getAcquisitionEnergySources()` instead.
     *
     * This method doesn't expect any request body.
     */
    getAcquisitionEnergySources$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AcquisitionFlowsEnergySourcesSelectElementService.GetAcquisitionEnergySourcesPath, 'get');
        if (params) {
            rb.query('callerId', params.callerId, {});
            rb.query('customerType', params.customerType, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get a list of available energy sources with additional cost related to each energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getAcquisitionEnergySources$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getAcquisitionEnergySources(params) {
        return this.getAcquisitionEnergySources$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getAcquisitionEnergySources
 */
AcquisitionFlowsEnergySourcesSelectElementService.GetAcquisitionEnergySourcesPath = '/elements/flows-energy-source-select-acquisition-section';
AcquisitionFlowsEnergySourcesSelectElementService.ɵfac = function AcquisitionFlowsEnergySourcesSelectElementService_Factory(t) { return new (t || AcquisitionFlowsEnergySourcesSelectElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
AcquisitionFlowsEnergySourcesSelectElementService.ɵprov = ɵɵdefineInjectable({ token: AcquisitionFlowsEnergySourcesSelectElementService, factory: AcquisitionFlowsEnergySourcesSelectElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(AcquisitionFlowsEnergySourcesSelectElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class AcquisitionFlowsProductSelectOptionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getAcquisitionProductForEnergySource()` instead.
     *
     * This method doesn't expect any request body.
     */
    getAcquisitionProductForEnergySource$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AcquisitionFlowsProductSelectOptionElementService.GetAcquisitionProductForEnergySourcePath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('callerId', params.callerId, {});
            rb.query('customerType', params.customerType, {});
            rb.query('energyArea', params.energyArea, {});
            rb.query('gridArea', params.gridArea, {});
            rb.query('estimatedKwhConsumption', params.estimatedKwhConsumption, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getAcquisitionProductForEnergySource$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getAcquisitionProductForEnergySource(params) {
        return this.getAcquisitionProductForEnergySource$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getAcquisitionProductForEnergySource
 */
AcquisitionFlowsProductSelectOptionElementService.GetAcquisitionProductForEnergySourcePath = '/elements/flows-product-select-option/acquisition/products/{productId}';
AcquisitionFlowsProductSelectOptionElementService.ɵfac = function AcquisitionFlowsProductSelectOptionElementService_Factory(t) { return new (t || AcquisitionFlowsProductSelectOptionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
AcquisitionFlowsProductSelectOptionElementService.ɵprov = ɵɵdefineInjectable({ token: AcquisitionFlowsProductSelectOptionElementService, factory: AcquisitionFlowsProductSelectOptionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(AcquisitionFlowsProductSelectOptionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class AssentlyConsumptionPowerOfAttorneyCallbackService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * When a case status is updated assently will call this endpoint and we will process the update if applicable.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `assentlyConsumptionPowerOfAttorneyCallbackPost$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    assentlyConsumptionPowerOfAttorneyCallbackPost$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AssentlyConsumptionPowerOfAttorneyCallbackService.AssentlyConsumptionPowerOfAttorneyCallbackPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'text',
            accept: '*/*'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: undefined });
        }));
    }
    /**
     * When a case status is updated assently will call this endpoint and we will process the update if applicable.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `assentlyConsumptionPowerOfAttorneyCallbackPost$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    assentlyConsumptionPowerOfAttorneyCallbackPost$Json(params) {
        return this.assentlyConsumptionPowerOfAttorneyCallbackPost$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * When a case status is updated assently will call this endpoint and we will process the update if applicable.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `assentlyConsumptionPowerOfAttorneyCallbackPost$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    assentlyConsumptionPowerOfAttorneyCallbackPost$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AssentlyConsumptionPowerOfAttorneyCallbackService.AssentlyConsumptionPowerOfAttorneyCallbackPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'text',
            accept: '*/*'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: undefined });
        }));
    }
    /**
     * When a case status is updated assently will call this endpoint and we will process the update if applicable.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `assentlyConsumptionPowerOfAttorneyCallbackPost$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    assentlyConsumptionPowerOfAttorneyCallbackPost$CspReport(params) {
        return this.assentlyConsumptionPowerOfAttorneyCallbackPost$CspReport$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation assentlyConsumptionPowerOfAttorneyCallbackPost
 */
AssentlyConsumptionPowerOfAttorneyCallbackService.AssentlyConsumptionPowerOfAttorneyCallbackPostPath = '/assently-consumption-power-of-attorney-callback';
AssentlyConsumptionPowerOfAttorneyCallbackService.ɵfac = function AssentlyConsumptionPowerOfAttorneyCallbackService_Factory(t) { return new (t || AssentlyConsumptionPowerOfAttorneyCallbackService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
AssentlyConsumptionPowerOfAttorneyCallbackService.ɵprov = ɵɵdefineInjectable({ token: AssentlyConsumptionPowerOfAttorneyCallbackService, factory: AssentlyConsumptionPowerOfAttorneyCallbackService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(AssentlyConsumptionPowerOfAttorneyCallbackService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class AverageSpotPriceService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the average spot prices for energy areas for given dates.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsAverageSpotPriceGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsAverageSpotPriceGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AverageSpotPriceService.ElementsAverageSpotPriceGetPath, 'get');
        if (params) {
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the average spot prices for energy areas for given dates.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsAverageSpotPriceGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsAverageSpotPriceGet(params) {
        return this.elementsAverageSpotPriceGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the postal code grid area element view model for the specified postal code.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsAverageSpotPricePostalCodesPostalCodeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsAverageSpotPricePostalCodesPostalCodeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AverageSpotPriceService.ElementsAverageSpotPricePostalCodesPostalCodeGetPath, 'get');
        if (params) {
            rb.path('postalCode', params.postalCode, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the postal code grid area element view model for the specified postal code.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsAverageSpotPricePostalCodesPostalCodeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsAverageSpotPricePostalCodesPostalCodeGet(params) {
        return this.elementsAverageSpotPricePostalCodesPostalCodeGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the postal code grid area element view model for the specified grid area.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsAverageSpotPriceGridAreasGridAreaGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsAverageSpotPriceGridAreasGridAreaGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, AverageSpotPriceService.ElementsAverageSpotPriceGridAreasGridAreaGetPath, 'get');
        if (params) {
            rb.path('gridArea', params.gridArea, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the postal code grid area element view model for the specified grid area.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsAverageSpotPriceGridAreasGridAreaGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsAverageSpotPriceGridAreasGridAreaGet(params) {
        return this.elementsAverageSpotPriceGridAreasGridAreaGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsAverageSpotPriceGet
 */
AverageSpotPriceService.ElementsAverageSpotPriceGetPath = '/elements/average-spot-price';
/**
 * Path part for operation elementsAverageSpotPricePostalCodesPostalCodeGet
 */
AverageSpotPriceService.ElementsAverageSpotPricePostalCodesPostalCodeGetPath = '/elements/average-spot-price/postal-codes/{postalCode}';
/**
 * Path part for operation elementsAverageSpotPriceGridAreasGridAreaGet
 */
AverageSpotPriceService.ElementsAverageSpotPriceGridAreasGridAreaGetPath = '/elements/average-spot-price/grid-areas/{gridArea}';
AverageSpotPriceService.ɵfac = function AverageSpotPriceService_Factory(t) { return new (t || AverageSpotPriceService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
AverageSpotPriceService.ɵprov = ɵɵdefineInjectable({ token: AverageSpotPriceService, factory: AverageSpotPriceService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(AverageSpotPriceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class ChangeFlowsEnergySourcesSelectElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get a list of available energy sources with additional cost related to each energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getChangeEnergySources()` instead.
     *
     * This method doesn't expect any request body.
     */
    getChangeEnergySources$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, ChangeFlowsEnergySourcesSelectElementService.GetChangeEnergySourcesPath, 'get');
        if (params) {
            rb.query('premiseId', params.premiseId, {});
            rb.query('callerId', params.callerId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get a list of available energy sources with additional cost related to each energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getChangeEnergySources$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getChangeEnergySources(params) {
        return this.getChangeEnergySources$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getChangeEnergySources
 */
ChangeFlowsEnergySourcesSelectElementService.GetChangeEnergySourcesPath = '/elements/flows-energy-source-select-change-section';
ChangeFlowsEnergySourcesSelectElementService.ɵfac = function ChangeFlowsEnergySourcesSelectElementService_Factory(t) { return new (t || ChangeFlowsEnergySourcesSelectElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
ChangeFlowsEnergySourcesSelectElementService.ɵprov = ɵɵdefineInjectable({ token: ChangeFlowsEnergySourcesSelectElementService, factory: ChangeFlowsEnergySourcesSelectElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ChangeFlowsEnergySourcesSelectElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class ChangeFlowsProductSelectOptionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the product for a specific productId, premiseId, callerId and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getChangeProductForEnergySource()` instead.
     *
     * This method doesn't expect any request body.
     */
    getChangeProductForEnergySource$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, ChangeFlowsProductSelectOptionElementService.GetChangeProductForEnergySourcePath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('premiseId', params.premiseId, {});
            rb.query('callerId', params.callerId, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, premiseId, callerId and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getChangeProductForEnergySource$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getChangeProductForEnergySource(params) {
        return this.getChangeProductForEnergySource$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getChangeProductForEnergySource
 */
ChangeFlowsProductSelectOptionElementService.GetChangeProductForEnergySourcePath = '/elements/flows-product-select-option/change/products/{productId}';
ChangeFlowsProductSelectOptionElementService.ɵfac = function ChangeFlowsProductSelectOptionElementService_Factory(t) { return new (t || ChangeFlowsProductSelectOptionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
ChangeFlowsProductSelectOptionElementService.ɵprov = ɵɵdefineInjectable({ token: ChangeFlowsProductSelectOptionElementService, factory: ChangeFlowsProductSelectOptionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ChangeFlowsProductSelectOptionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class ClimateService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the outdoor temperature for the specified postal code.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsClimateTemperatureTimeSpanZipCodeZipCodeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsClimateTemperatureTimeSpanZipCodeZipCodeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, ClimateService.ElementsClimateTemperatureTimeSpanZipCodeZipCodeGetPath, 'get');
        if (params) {
            rb.path('timeSpan', params.timeSpan, {});
            rb.path('zipCode', params.zipCode, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the outdoor temperature for the specified postal code.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsClimateTemperatureTimeSpanZipCodeZipCodeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsClimateTemperatureTimeSpanZipCodeZipCodeGet(params) {
        return this.elementsClimateTemperatureTimeSpanZipCodeZipCodeGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsClimateTemperatureTimeSpanZipCodeZipCodeGet
 */
ClimateService.ElementsClimateTemperatureTimeSpanZipCodeZipCodeGetPath = '/elements/climate/temperature/{timeSpan}/zipCode/{zipCode}';
ClimateService.ɵfac = function ClimateService_Factory(t) { return new (t || ClimateService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
ClimateService.ɵprov = ɵɵdefineInjectable({ token: ClimateService, factory: ClimateService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ClimateService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class CspReportService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Content Security Report uri end-point.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `apiCspReportReportUriPost$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    apiCspReportReportUriPost$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, CspReportService.ApiCspReportReportUriPostPath, 'post');
        if (params) {
            rb.query('key', params.key, {});
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Content Security Report uri end-point.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `apiCspReportReportUriPost$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    apiCspReportReportUriPost$Json(params) {
        return this.apiCspReportReportUriPost$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Content Security Report uri end-point.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `apiCspReportReportUriPost$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    apiCspReportReportUriPost$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, CspReportService.ApiCspReportReportUriPostPath, 'post');
        if (params) {
            rb.query('key', params.key, {});
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Content Security Report uri end-point.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `apiCspReportReportUriPost$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    apiCspReportReportUriPost$CspReport(params) {
        return this.apiCspReportReportUriPost$CspReport$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation apiCspReportReportUriPost
 */
CspReportService.ApiCspReportReportUriPostPath = '/api/csp-report/report-uri';
CspReportService.ɵfac = function CspReportService_Factory(t) { return new (t || CspReportService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
CspReportService.ɵprov = ɵɵdefineInjectable({ token: CspReportService, factory: CspReportService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(CspReportService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class DynamicDataTableService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the price lists for open pages.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsDynamicDataTablePriceListTypesTypeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsDynamicDataTablePriceListTypesTypeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, DynamicDataTableService.ElementsDynamicDataTablePriceListTypesTypeGetPath, 'get');
        if (params) {
            rb.path('type', params.type, {});
            rb.query('productId', params.productId, {});
            rb.query('energyArea', params.energyArea, {});
            rb.query('energySource', params.energySource, {});
            rb.query('consumption', params.consumption, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the price lists for open pages.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsDynamicDataTablePriceListTypesTypeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsDynamicDataTablePriceListTypesTypeGet(params) {
        return this.elementsDynamicDataTablePriceListTypesTypeGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsDynamicDataTablePriceListTypesTypeGet
 */
DynamicDataTableService.ElementsDynamicDataTablePriceListTypesTypeGetPath = '/elements/dynamic-data-table/price-list/types/{type}';
DynamicDataTableService.ɵfac = function DynamicDataTableService_Factory(t) { return new (t || DynamicDataTableService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
DynamicDataTableService.ɵprov = ɵɵdefineInjectable({ token: DynamicDataTableService, factory: DynamicDataTableService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DynamicDataTableService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlexAppService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * FLEX - Get temperatures for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionClimateTemperaturePremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionClimateTemperaturePremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionClimateTemperaturePremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get temperatures for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionClimateTemperaturePremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionClimateTemperaturePremisesPremiseIdGet(params) {
        return this.flexVVersionClimateTemperaturePremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get latest temperature and wind information for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionClimateLatestWeatherInfoPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionClimateLatestWeatherInfoPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionClimateLatestWeatherInfoPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get latest temperature and wind information for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionClimateLatestWeatherInfoPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionClimateLatestWeatherInfoPremisesPremiseIdGet(params) {
        return this.flexVVersionClimateLatestWeatherInfoPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get latest temperature and wind information for a zip code.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionClimateLatestWeatherInfoZipCodeZipCodeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionClimateLatestWeatherInfoZipCodeZipCodeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionClimateLatestWeatherInfoZipCodeZipCodeGetPath, 'get');
        if (params) {
            rb.path('zipCode', params.zipCode, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get latest temperature and wind information for a zip code.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionClimateLatestWeatherInfoZipCodeZipCodeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionClimateLatestWeatherInfoZipCodeZipCodeGet(params) {
        return this.flexVVersionClimateLatestWeatherInfoZipCodeZipCodeGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get electricity consumption for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionConsumptionPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionConsumptionPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionConsumptionPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
            rb.query('dataSourceOption', params.dataSourceOption, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get electricity consumption for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionConsumptionPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionConsumptionPremisesPremiseIdGet(params) {
        return this.flexVVersionConsumptionPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get cost for consumption for a premise for a given resolution.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionConsumptionCostPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionConsumptionCostPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionConsumptionCostPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
            rb.query('dataSourceOption', params.dataSourceOption, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get cost for consumption for a premise for a given resolution.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionConsumptionCostPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionConsumptionCostPremisesPremiseIdGet(params) {
        return this.flexVVersionConsumptionCostPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get all renewable premises associated with the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionContractChangeFlowsRenewablePremisesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionContractChangeFlowsRenewablePremisesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionContractChangeFlowsRenewablePremisesGetPath, 'get');
        if (params) {
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get all renewable premises associated with the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionContractChangeFlowsRenewablePremisesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionContractChangeFlowsRenewablePremisesGet(params) {
        return this.flexVVersionContractChangeFlowsRenewablePremisesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get the contracts for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionContractsPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionContractsPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionContractsPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('includeFutureContracts', params.includeFutureContracts, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get the contracts for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionContractsPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionContractsPremisesPremiseIdGet(params) {
        return this.flexVVersionContractsPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get invoice settings information for a premise for the logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionCustomersInvoiceSettingsPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionCustomersInvoiceSettingsPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionCustomersInvoiceSettingsPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get invoice settings information for a premise for the logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionCustomersInvoiceSettingsPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionCustomersInvoiceSettingsPremisesPremiseIdGet(params) {
        return this.flexVVersionCustomersInvoiceSettingsPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get measurement ranges for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionEnergyPremisesPremiseIdMeasurementRangesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionEnergyPremisesPremiseIdMeasurementRangesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionEnergyPremisesPremiseIdMeasurementRangesGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get measurement ranges for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionEnergyPremisesPremiseIdMeasurementRangesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionEnergyPremisesPremiseIdMeasurementRangesGet(params) {
        return this.flexVVersionEnergyPremisesPremiseIdMeasurementRangesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get measurement for premise for specified resolution, measurementType and start and end dates.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionEnergyPremisesPremiseIdMeasurementGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionEnergyPremisesPremiseIdMeasurementGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionEnergyPremisesPremiseIdMeasurementGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('resolution', params.resolution, {});
            rb.query('measurementType', params.measurementType, {});
            rb.query('startDate', params.startDate, {});
            rb.query('endDate', params.endDate, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get measurement for premise for specified resolution, measurementType and start and end dates.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionEnergyPremisesPremiseIdMeasurementGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionEnergyPremisesPremiseIdMeasurementGet(params) {
        return this.flexVVersionEnergyPremisesPremiseIdMeasurementGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionHourlySpotPricesPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionHourlySpotPricesPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionHourlySpotPricesPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionHourlySpotPricesPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionHourlySpotPricesPremisesPremiseIdGet(params) {
        return this.flexVVersionHourlySpotPricesPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get customer specific hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get customer specific hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGet(params) {
        return this.flexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get hourly spot prices for energy area.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionHourlySpotPricesEnergyAreaEnergyAreaGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionHourlySpotPricesEnergyAreaEnergyAreaGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionHourlySpotPricesEnergyAreaEnergyAreaGetPath, 'get');
        if (params) {
            rb.path('energyArea', params.energyArea, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get hourly spot prices for energy area.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionHourlySpotPricesEnergyAreaEnergyAreaGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionHourlySpotPricesEnergyAreaEnergyAreaGet(params) {
        return this.flexVVersionHourlySpotPricesEnergyAreaEnergyAreaGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get the invoices for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionInvoicesPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionInvoicesPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionInvoicesPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get the invoices for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionInvoicesPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionInvoicesPremisesPremiseIdGet(params) {
        return this.flexVVersionInvoicesPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get the invoice file content for the specified invoice id for the customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionInvoicesArchiveDocumentIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionInvoicesArchiveDocumentIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionInvoicesArchiveDocumentIdGetPath, 'get');
        if (params) {
            rb.path('archiveDocumentId', params.archiveDocumentId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get the invoice file content for the specified invoice id for the customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionInvoicesArchiveDocumentIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionInvoicesArchiveDocumentIdGet(params) {
        return this.flexVVersionInvoicesArchiveDocumentIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get measurement ranges for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get measurement ranges for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGet(params) {
        return this.flexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get measurement for premise for specified resolution, measurementType and start and end dates.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionMicroproductionPremisesPremiseIdMeasurementGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionMicroproductionPremisesPremiseIdMeasurementGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionMicroproductionPremisesPremiseIdMeasurementGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('resolution', params.resolution, {});
            rb.query('measurementType', params.measurementType, {});
            rb.query('startDate', params.startDate, {});
            rb.query('endDate', params.endDate, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get measurement for premise for specified resolution, measurementType and start and end dates.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionMicroproductionPremisesPremiseIdMeasurementGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionMicroproductionPremisesPremiseIdMeasurementGet(params) {
        return this.flexVVersionMicroproductionPremisesPremiseIdMeasurementGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get customer premises information.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesGetPath, 'get');
        if (params) {
            rb.query('includeFutureContracts', params.includeFutureContracts, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get customer premises information.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesGet(params) {
        return this.flexVVersionPremisesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get energy area of the specified premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPremiseIdEnergyAreaGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdEnergyAreaGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPremiseIdEnergyAreaGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get energy area of the specified premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPremiseIdEnergyAreaGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdEnergyAreaGet(params) {
        return this.flexVVersionPremisesPremiseIdEnergyAreaGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get contract type of the specified premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPremiseIdContractTypeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdContractTypeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPremiseIdContractTypeGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get contract type of the specified premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPremiseIdContractTypeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdContractTypeGet(params) {
        return this.flexVVersionPremisesPremiseIdContractTypeGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get contract type of the specified premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPremiseIdGridOwnerGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdGridOwnerGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPremiseIdGridOwnerGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get contract type of the specified premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPremiseIdGridOwnerGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdGridOwnerGet(params) {
        return this.flexVVersionPremisesPremiseIdGridOwnerGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get electricity start date for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPremiseIdElectricityStartDateGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdElectricityStartDateGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPremiseIdElectricityStartDateGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get electricity start date for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPremiseIdElectricityStartDateGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdElectricityStartDateGet(params) {
        return this.flexVVersionPremisesPremiseIdElectricityStartDateGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get available resolution for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPremiseIdResolutionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdResolutionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPremiseIdResolutionGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get available resolution for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPremiseIdResolutionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdResolutionGet(params) {
        return this.flexVVersionPremisesPremiseIdResolutionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get position of a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPremiseIdPositionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdPositionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPremiseIdPositionGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get position of a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPremiseIdPositionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPremiseIdPositionGet(params) {
        return this.flexVVersionPremisesPremiseIdPositionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get position of a zipcode.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPremisesPositionZipCodeZipCodeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPositionZipCodeZipCodeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionPremisesPositionZipCodeZipCodeGetPath, 'get');
        if (params) {
            rb.path('zipCode', params.zipCode, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get position of a zipcode.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPremisesPositionZipCodeZipCodeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPremisesPositionZipCodeZipCodeGet(params) {
        return this.flexVVersionPremisesPositionZipCodeZipCodeGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get profile information for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionProfileGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionProfileGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionProfileGetPath, 'get');
        if (params) {
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get profile information for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionProfileGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionProfileGet(params) {
        return this.flexVVersionProfileGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Get logged in customer detail information.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionProfileSummaryGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionProfileSummaryGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionProfileSummaryGetPath, 'get');
        if (params) {
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Get logged in customer detail information.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionProfileSummaryGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionProfileSummaryGet(params) {
        return this.flexVVersionProfileSummaryGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * FLEX - Check whether the user is a Guest User.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionUsersRoleGuestGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionUsersRoleGuestGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppService.FlexVVersionUsersRoleGuestGetPath, 'get');
        if (params) {
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * FLEX - Check whether the user is a Guest User.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionUsersRoleGuestGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionUsersRoleGuestGet(params) {
        return this.flexVVersionUsersRoleGuestGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation flexVVersionClimateTemperaturePremisesPremiseIdGet
 */
FlexAppService.FlexVVersionClimateTemperaturePremisesPremiseIdGetPath = '/flex/v{version}/climate/temperature/premises/{premiseId}';
/**
 * Path part for operation flexVVersionClimateLatestWeatherInfoPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionClimateLatestWeatherInfoPremisesPremiseIdGetPath = '/flex/v{version}/climate/latest-weather-info/premises/{premiseId}';
/**
 * Path part for operation flexVVersionClimateLatestWeatherInfoZipCodeZipCodeGet
 */
FlexAppService.FlexVVersionClimateLatestWeatherInfoZipCodeZipCodeGetPath = '/flex/v{version}/climate/latest-weather-info/zipCode/{zipCode}';
/**
 * Path part for operation flexVVersionConsumptionPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionConsumptionPremisesPremiseIdGetPath = '/flex/v{version}/consumption/premises/{premiseId}';
/**
 * Path part for operation flexVVersionConsumptionCostPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionConsumptionCostPremisesPremiseIdGetPath = '/flex/v{version}/consumption/cost/premises/{premiseId}';
/**
 * Path part for operation flexVVersionContractChangeFlowsRenewablePremisesGet
 */
FlexAppService.FlexVVersionContractChangeFlowsRenewablePremisesGetPath = '/flex/v{version}/contract-change-flows/renewable-premises';
/**
 * Path part for operation flexVVersionContractsPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionContractsPremisesPremiseIdGetPath = '/flex/v{version}/contracts/premises/{premiseId}';
/**
 * Path part for operation flexVVersionCustomersInvoiceSettingsPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionCustomersInvoiceSettingsPremisesPremiseIdGetPath = '/flex/v{version}/customers/invoice-settings/premises/{premiseId}';
/**
 * Path part for operation flexVVersionEnergyPremisesPremiseIdMeasurementRangesGet
 */
FlexAppService.FlexVVersionEnergyPremisesPremiseIdMeasurementRangesGetPath = '/flex/v{version}/energy/premises/{premiseId}/measurement-ranges';
/**
 * Path part for operation flexVVersionEnergyPremisesPremiseIdMeasurementGet
 */
FlexAppService.FlexVVersionEnergyPremisesPremiseIdMeasurementGetPath = '/flex/v{version}/energy/premises/{premiseId}/measurement';
/**
 * Path part for operation flexVVersionHourlySpotPricesPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionHourlySpotPricesPremisesPremiseIdGetPath = '/flex/v{version}/hourly-spot-prices/premises/{premiseId}';
/**
 * Path part for operation flexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionHourlySpotPricesCustomerSpecificPremisesPremiseIdGetPath = '/flex/v{version}/hourly-spot-prices/customer-specific/premises/{premiseId}';
/**
 * Path part for operation flexVVersionHourlySpotPricesEnergyAreaEnergyAreaGet
 */
FlexAppService.FlexVVersionHourlySpotPricesEnergyAreaEnergyAreaGetPath = '/flex/v{version}/hourly-spot-prices/energy-area/{energyArea}';
/**
 * Path part for operation flexVVersionInvoicesPremisesPremiseIdGet
 */
FlexAppService.FlexVVersionInvoicesPremisesPremiseIdGetPath = '/flex/v{version}/invoices/premises/{premiseId}';
/**
 * Path part for operation flexVVersionInvoicesArchiveDocumentIdGet
 */
FlexAppService.FlexVVersionInvoicesArchiveDocumentIdGetPath = '/flex/v{version}/invoices/{archiveDocumentId}';
/**
 * Path part for operation flexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGet
 */
FlexAppService.FlexVVersionMicroproductionPremisesPremiseIdMeasurementRangesGetPath = '/flex/v{version}/microproduction/premises/{premiseId}/measurement-ranges';
/**
 * Path part for operation flexVVersionMicroproductionPremisesPremiseIdMeasurementGet
 */
FlexAppService.FlexVVersionMicroproductionPremisesPremiseIdMeasurementGetPath = '/flex/v{version}/microproduction/premises/{premiseId}/measurement';
/**
 * Path part for operation flexVVersionPremisesGet
 */
FlexAppService.FlexVVersionPremisesGetPath = '/flex/v{version}/premises';
/**
 * Path part for operation flexVVersionPremisesPremiseIdEnergyAreaGet
 */
FlexAppService.FlexVVersionPremisesPremiseIdEnergyAreaGetPath = '/flex/v{version}/premises/{premiseId}/energy-area';
/**
 * Path part for operation flexVVersionPremisesPremiseIdContractTypeGet
 */
FlexAppService.FlexVVersionPremisesPremiseIdContractTypeGetPath = '/flex/v{version}/premises/{premiseId}/contract-type';
/**
 * Path part for operation flexVVersionPremisesPremiseIdGridOwnerGet
 */
FlexAppService.FlexVVersionPremisesPremiseIdGridOwnerGetPath = '/flex/v{version}/premises/{premiseId}/grid-owner';
/**
 * Path part for operation flexVVersionPremisesPremiseIdElectricityStartDateGet
 */
FlexAppService.FlexVVersionPremisesPremiseIdElectricityStartDateGetPath = '/flex/v{version}/premises/{premiseId}/electricity-start-date';
/**
 * Path part for operation flexVVersionPremisesPremiseIdResolutionGet
 */
FlexAppService.FlexVVersionPremisesPremiseIdResolutionGetPath = '/flex/v{version}/premises/{premiseId}/resolution';
/**
 * Path part for operation flexVVersionPremisesPremiseIdPositionGet
 */
FlexAppService.FlexVVersionPremisesPremiseIdPositionGetPath = '/flex/v{version}/premises/{premiseId}/position';
/**
 * Path part for operation flexVVersionPremisesPositionZipCodeZipCodeGet
 */
FlexAppService.FlexVVersionPremisesPositionZipCodeZipCodeGetPath = '/flex/v{version}/premises/position/zip-code/{zipCode}';
/**
 * Path part for operation flexVVersionProfileGet
 */
FlexAppService.FlexVVersionProfileGetPath = '/flex/v{version}/profile';
/**
 * Path part for operation flexVVersionProfileSummaryGet
 */
FlexAppService.FlexVVersionProfileSummaryGetPath = '/flex/v{version}/profile/summary';
/**
 * Path part for operation flexVVersionUsersRoleGuestGet
 */
FlexAppService.FlexVVersionUsersRoleGuestGetPath = '/flex/v{version}/users/role/guest';
FlexAppService.ɵfac = function FlexAppService_Factory(t) { return new (t || FlexAppService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlexAppService.ɵprov = ɵɵdefineInjectable({ token: FlexAppService, factory: FlexAppService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlexAppService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlexAppPowerOfAttorneyService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get power of attorney status for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPowerOfAttorneyPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPowerOfAttorneyPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppPowerOfAttorneyService.FlexVVersionPowerOfAttorneyPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get power of attorney status for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPowerOfAttorneyPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPowerOfAttorneyPremisesPremiseIdGet(params) {
        return this.flexVVersionPowerOfAttorneyPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Start power of attorney process for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `flexVVersionPowerOfAttorneyPremisesPremiseIdPut()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPowerOfAttorneyPremisesPremiseIdPut$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlexAppPowerOfAttorneyService.FlexVVersionPowerOfAttorneyPremisesPremiseIdPutPath, 'put');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.path('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Start power of attorney process for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `flexVVersionPowerOfAttorneyPremisesPremiseIdPut$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    flexVVersionPowerOfAttorneyPremisesPremiseIdPut(params) {
        return this.flexVVersionPowerOfAttorneyPremisesPremiseIdPut$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation flexVVersionPowerOfAttorneyPremisesPremiseIdGet
 */
FlexAppPowerOfAttorneyService.FlexVVersionPowerOfAttorneyPremisesPremiseIdGetPath = '/flex/v{version}/power-of-attorney/premises/{premiseId}';
/**
 * Path part for operation flexVVersionPowerOfAttorneyPremisesPremiseIdPut
 */
FlexAppPowerOfAttorneyService.FlexVVersionPowerOfAttorneyPremisesPremiseIdPutPath = '/flex/v{version}/power-of-attorney/premises/{premiseId}';
FlexAppPowerOfAttorneyService.ɵfac = function FlexAppPowerOfAttorneyService_Factory(t) { return new (t || FlexAppPowerOfAttorneyService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlexAppPowerOfAttorneyService.ɵprov = ɵɵdefineInjectable({ token: FlexAppPowerOfAttorneyService, factory: FlexAppPowerOfAttorneyService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlexAppPowerOfAttorneyService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsAcquisitionBusinessCheckoutService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsAcquisitionBusinessCheckoutService.ElementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGetPath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('callerId', params.callerId, {});
            rb.query('gridArea', params.gridArea, {});
            rb.query('estimatedKwhConsumption', params.estimatedKwhConsumption, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGet(params) {
        return this.elementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPost()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPost$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsAcquisitionBusinessCheckoutService.ElementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPost$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPost(params) {
        return this.elementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPost$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGet
 */
FlowsAcquisitionBusinessCheckoutService.ElementsFlowsAcquisitionBusinessSwedenCheckoutProductsProductIdGetPath = '/elements/flows-acquisition-business-sweden-checkout/products/{productId}';
/**
 * Path part for operation elementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPost
 */
FlowsAcquisitionBusinessCheckoutService.ElementsFlowsAcquisitionBusinessSwedenCheckoutOrdersPostPath = '/elements/flows-acquisition-business-sweden-checkout/orders';
FlowsAcquisitionBusinessCheckoutService.ɵfac = function FlowsAcquisitionBusinessCheckoutService_Factory(t) { return new (t || FlowsAcquisitionBusinessCheckoutService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsAcquisitionBusinessCheckoutService.ɵprov = ɵɵdefineInjectable({ token: FlowsAcquisitionBusinessCheckoutService, factory: FlowsAcquisitionBusinessCheckoutService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsAcquisitionBusinessCheckoutService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsAcquisitionPrivateCheckoutService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get a list of allowed e-invoice banks.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsAcquisitionPrivateCheckoutService.ElementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get a list of allowed e-invoice banks.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet(params) {
        return this.elementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsAcquisitionPrivateCheckoutService.ElementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGetPath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('callerId', params.callerId, {});
            rb.query('energyArea', params.energyArea, {});
            rb.query('gridArea', params.gridArea, {});
            rb.query('estimatedKwhConsumption', params.estimatedKwhConsumption, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGet(params) {
        return this.elementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPost()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPost$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsAcquisitionPrivateCheckoutService.ElementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPost$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPost(params) {
        return this.elementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPost$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet
 */
FlowsAcquisitionPrivateCheckoutService.ElementsFlowsAcquisitionPrivateSwedenCheckoutInvoiceSettingsPaymentBanksGetPath = '/elements/flows-acquisition-private-sweden-checkout/invoice-settings/payment/banks';
/**
 * Path part for operation elementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGet
 */
FlowsAcquisitionPrivateCheckoutService.ElementsFlowsAcquisitionPrivateSwedenCheckoutProductsProductIdGetPath = '/elements/flows-acquisition-private-sweden-checkout/products/{productId}';
/**
 * Path part for operation elementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPost
 */
FlowsAcquisitionPrivateCheckoutService.ElementsFlowsAcquisitionPrivateSwedenCheckoutOrdersPostPath = '/elements/flows-acquisition-private-sweden-checkout/orders';
FlowsAcquisitionPrivateCheckoutService.ɵfac = function FlowsAcquisitionPrivateCheckoutService_Factory(t) { return new (t || FlowsAcquisitionPrivateCheckoutService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsAcquisitionPrivateCheckoutService.ɵprov = ɵɵdefineInjectable({ token: FlowsAcquisitionPrivateCheckoutService, factory: FlowsAcquisitionPrivateCheckoutService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsAcquisitionPrivateCheckoutService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsChangeContractCheckoutService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsChangeContractSwedenCheckoutOrdersPost()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsChangeContractSwedenCheckoutOrdersPost$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsChangeContractCheckoutService.ElementsFlowsChangeContractSwedenCheckoutOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsChangeContractSwedenCheckoutOrdersPost$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsChangeContractSwedenCheckoutOrdersPost(params) {
        return this.elementsFlowsChangeContractSwedenCheckoutOrdersPost$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsChangeContractSwedenCheckoutOrdersPost
 */
FlowsChangeContractCheckoutService.ElementsFlowsChangeContractSwedenCheckoutOrdersPostPath = '/elements/flows-change-contract-sweden-checkout/orders';
FlowsChangeContractCheckoutService.ɵfac = function FlowsChangeContractCheckoutService_Factory(t) { return new (t || FlowsChangeContractCheckoutService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsChangeContractCheckoutService.ɵprov = ɵɵdefineInjectable({ token: FlowsChangeContractCheckoutService, factory: FlowsChangeContractCheckoutService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsChangeContractCheckoutService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsContractDetailsSectionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get details about the renewable contract for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsContractDetailsSectionPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsContractDetailsSectionPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsContractDetailsSectionElementService.ElementsFlowsContractDetailsSectionPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get details about the renewable contract for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsContractDetailsSectionPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsContractDetailsSectionPremisesPremiseIdGet(params) {
        return this.elementsFlowsContractDetailsSectionPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsContractDetailsSectionPremisesPremiseIdGet
 */
FlowsContractDetailsSectionElementService.ElementsFlowsContractDetailsSectionPremisesPremiseIdGetPath = '/elements/flows-contract-details-section/premises/{premiseId}';
FlowsContractDetailsSectionElementService.ɵfac = function FlowsContractDetailsSectionElementService_Factory(t) { return new (t || FlowsContractDetailsSectionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsContractDetailsSectionElementService.ɵprov = ɵɵdefineInjectable({ token: FlowsContractDetailsSectionElementService, factory: FlowsContractDetailsSectionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsContractDetailsSectionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsMoveAcquisitionPrivateSwitchService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get estimated yearly consumption values for a building type and for specific living areas.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsMoveAcquisitionPrivateSwitchService.ElementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGetPath, 'get');
        if (params) {
            rb.path('buildingType', params.buildingType, {});
            rb.query('postalCode', params.postalCode, {});
            rb.query('livingArea', params.livingArea, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get estimated yearly consumption values for a building type and for specific living areas.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet(params) {
        return this.elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGet
 */
FlowsMoveAcquisitionPrivateSwitchService.ElementsFlowsMoveAcquisitionPrivateSwitchEstimatedConsumptionBuildingTypesBuildingTypeGetPath = '/elements/flows-move-acquisition-private-switch/estimated-consumption/building-types/{buildingType}';
FlowsMoveAcquisitionPrivateSwitchService.ɵfac = function FlowsMoveAcquisitionPrivateSwitchService_Factory(t) { return new (t || FlowsMoveAcquisitionPrivateSwitchService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsMoveAcquisitionPrivateSwitchService.ɵprov = ɵɵdefineInjectable({ token: FlowsMoveAcquisitionPrivateSwitchService, factory: FlowsMoveAcquisitionPrivateSwitchService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsMoveAcquisitionPrivateSwitchService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsMoveBusinessCheckoutService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsMoveBusinessCheckoutService.ElementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGetPath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('callerId', params.callerId, {});
            rb.query('gridArea', params.gridArea, {});
            rb.query('estimatedKwhConsumption', params.estimatedKwhConsumption, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGet(params) {
        return this.elementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsMoveBusinessSwedenCheckoutOrdersPost()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsMoveBusinessSwedenCheckoutOrdersPost$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsMoveBusinessCheckoutService.ElementsFlowsMoveBusinessSwedenCheckoutOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsMoveBusinessSwedenCheckoutOrdersPost$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsMoveBusinessSwedenCheckoutOrdersPost(params) {
        return this.elementsFlowsMoveBusinessSwedenCheckoutOrdersPost$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGet
 */
FlowsMoveBusinessCheckoutService.ElementsFlowsMoveBusinessSwedenCheckoutProductsProductIdGetPath = '/elements/flows-move-business-sweden-checkout/products/{productId}';
/**
 * Path part for operation elementsFlowsMoveBusinessSwedenCheckoutOrdersPost
 */
FlowsMoveBusinessCheckoutService.ElementsFlowsMoveBusinessSwedenCheckoutOrdersPostPath = '/elements/flows-move-business-sweden-checkout/orders';
FlowsMoveBusinessCheckoutService.ɵfac = function FlowsMoveBusinessCheckoutService_Factory(t) { return new (t || FlowsMoveBusinessCheckoutService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsMoveBusinessCheckoutService.ɵprov = ɵɵdefineInjectable({ token: FlowsMoveBusinessCheckoutService, factory: FlowsMoveBusinessCheckoutService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsMoveBusinessCheckoutService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsMovePrivateCheckoutService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get a list of allowed e-invoice banks.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsMovePrivateCheckoutService.ElementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get a list of allowed e-invoice banks.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet(params) {
        return this.elementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsMovePrivateSwedenCheckoutProductsProductIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMovePrivateSwedenCheckoutProductsProductIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsMovePrivateCheckoutService.ElementsFlowsMovePrivateSwedenCheckoutProductsProductIdGetPath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('callerId', params.callerId, {});
            rb.query('energyArea', params.energyArea, {});
            rb.query('gridArea', params.gridArea, {});
            rb.query('estimatedKwhConsumption', params.estimatedKwhConsumption, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, callerId, customerType, gridArea, estimatedKwhConsumption and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsMovePrivateSwedenCheckoutProductsProductIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsMovePrivateSwedenCheckoutProductsProductIdGet(params) {
        return this.elementsFlowsMovePrivateSwedenCheckoutProductsProductIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsMovePrivateSwedenCheckoutOrdersPost()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsMovePrivateSwedenCheckoutOrdersPost$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsMovePrivateCheckoutService.ElementsFlowsMovePrivateSwedenCheckoutOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsMovePrivateSwedenCheckoutOrdersPost$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsMovePrivateSwedenCheckoutOrdersPost(params) {
        return this.elementsFlowsMovePrivateSwedenCheckoutOrdersPost$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGet
 */
FlowsMovePrivateCheckoutService.ElementsFlowsMovePrivateSwedenCheckoutInvoiceSettingsPaymentBanksGetPath = '/elements/flows-move-private-sweden-checkout/invoice-settings/payment/banks';
/**
 * Path part for operation elementsFlowsMovePrivateSwedenCheckoutProductsProductIdGet
 */
FlowsMovePrivateCheckoutService.ElementsFlowsMovePrivateSwedenCheckoutProductsProductIdGetPath = '/elements/flows-move-private-sweden-checkout/products/{productId}';
/**
 * Path part for operation elementsFlowsMovePrivateSwedenCheckoutOrdersPost
 */
FlowsMovePrivateCheckoutService.ElementsFlowsMovePrivateSwedenCheckoutOrdersPostPath = '/elements/flows-move-private-sweden-checkout/orders';
FlowsMovePrivateCheckoutService.ɵfac = function FlowsMovePrivateCheckoutService_Factory(t) { return new (t || FlowsMovePrivateCheckoutService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsMovePrivateCheckoutService.ɵprov = ɵɵdefineInjectable({ token: FlowsMovePrivateCheckoutService, factory: FlowsMovePrivateCheckoutService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsMovePrivateCheckoutService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsNewContractDetailsSectionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get possible delivery start and end dates for acquisition flow or move flow.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsNewContractDetailsSectionDeliverydatesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsNewContractDetailsSectionDeliverydatesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsNewContractDetailsSectionElementService.ElementsFlowsNewContractDetailsSectionDeliverydatesGetPath, 'get');
        if (params) {
            rb.query('isMoveFlow', params.isMoveFlow, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get possible delivery start and end dates for acquisition flow or move flow.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsNewContractDetailsSectionDeliverydatesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsNewContractDetailsSectionDeliverydatesGet(params) {
        return this.elementsFlowsNewContractDetailsSectionDeliverydatesGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsNewContractDetailsSectionDeliverydatesGet
 */
FlowsNewContractDetailsSectionElementService.ElementsFlowsNewContractDetailsSectionDeliverydatesGetPath = '/elements/flows-new-contract-details-section/deliverydates';
FlowsNewContractDetailsSectionElementService.ɵfac = function FlowsNewContractDetailsSectionElementService_Factory(t) { return new (t || FlowsNewContractDetailsSectionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsNewContractDetailsSectionElementService.ɵprov = ɵɵdefineInjectable({ token: FlowsNewContractDetailsSectionElementService, factory: FlowsNewContractDetailsSectionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsNewContractDetailsSectionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsPremiseContractSummaryElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get premise- and contract- summary information for a given premise owned by the logged in customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsPremiseContractSummaryPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremiseContractSummaryPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsPremiseContractSummaryElementService.ElementsFlowsPremiseContractSummaryPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get premise- and contract- summary information for a given premise owned by the logged in customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsPremiseContractSummaryPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremiseContractSummaryPremisesPremiseIdGet(params) {
        return this.elementsFlowsPremiseContractSummaryPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsPremiseContractSummaryPremisesPremiseIdGet
 */
FlowsPremiseContractSummaryElementService.ElementsFlowsPremiseContractSummaryPremisesPremiseIdGetPath = '/elements/flows-premise-contract-summary/premises/{premiseId}';
FlowsPremiseContractSummaryElementService.ɵfac = function FlowsPremiseContractSummaryElementService_Factory(t) { return new (t || FlowsPremiseContractSummaryElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsPremiseContractSummaryElementService.ɵprov = ɵɵdefineInjectable({ token: FlowsPremiseContractSummaryElementService, factory: FlowsPremiseContractSummaryElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsPremiseContractSummaryElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsPremisesSelectElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get all renewable premises associated with the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsPremisesSelectRenewalGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremisesSelectRenewalGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsPremisesSelectElementService.ElementsFlowsPremisesSelectRenewalGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get all renewable premises associated with the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsPremisesSelectRenewalGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremisesSelectRenewalGet(params) {
        return this.elementsFlowsPremisesSelectRenewalGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get all premises eligible for winter assurance associated with the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsPremisesSelectWinterAssuranceGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremisesSelectWinterAssuranceGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsPremisesSelectElementService.ElementsFlowsPremisesSelectWinterAssuranceGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get all premises eligible for winter assurance associated with the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsPremisesSelectWinterAssuranceGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremisesSelectWinterAssuranceGet(params) {
        return this.elementsFlowsPremisesSelectWinterAssuranceGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get all premises eligible for product change associated with the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsPremisesSelectChangeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremisesSelectChangeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsPremisesSelectElementService.ElementsFlowsPremisesSelectChangeGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get all premises eligible for product change associated with the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsPremisesSelectChangeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsPremisesSelectChangeGet(params) {
        return this.elementsFlowsPremisesSelectChangeGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsPremisesSelectRenewalGet
 */
FlowsPremisesSelectElementService.ElementsFlowsPremisesSelectRenewalGetPath = '/elements/flows-premises-select/renewal';
/**
 * Path part for operation elementsFlowsPremisesSelectWinterAssuranceGet
 */
FlowsPremisesSelectElementService.ElementsFlowsPremisesSelectWinterAssuranceGetPath = '/elements/flows-premises-select/winter-assurance';
/**
 * Path part for operation elementsFlowsPremisesSelectChangeGet
 */
FlowsPremisesSelectElementService.ElementsFlowsPremisesSelectChangeGetPath = '/elements/flows-premises-select/change';
FlowsPremisesSelectElementService.ɵfac = function FlowsPremisesSelectElementService_Factory(t) { return new (t || FlowsPremisesSelectElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsPremisesSelectElementService.ɵprov = ɵɵdefineInjectable({ token: FlowsPremisesSelectElementService, factory: FlowsPremisesSelectElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsPremisesSelectElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsProductAddonsSectionViewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get contact information for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsProductAddonsSectionCustomerContactInformationGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsProductAddonsSectionCustomerContactInformationGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsProductAddonsSectionViewElementService.ElementsFlowsProductAddonsSectionCustomerContactInformationGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get contact information for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsProductAddonsSectionCustomerContactInformationGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsFlowsProductAddonsSectionCustomerContactInformationGet(params) {
        return this.elementsFlowsProductAddonsSectionCustomerContactInformationGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsProductAddonsSectionCustomerContactInformationGet
 */
FlowsProductAddonsSectionViewElementService.ElementsFlowsProductAddonsSectionCustomerContactInformationGetPath = '/elements/flows-product-addons-section/customer/contact-information';
FlowsProductAddonsSectionViewElementService.ɵfac = function FlowsProductAddonsSectionViewElementService_Factory(t) { return new (t || FlowsProductAddonsSectionViewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsProductAddonsSectionViewElementService.ɵprov = ɵɵdefineInjectable({ token: FlowsProductAddonsSectionViewElementService, factory: FlowsProductAddonsSectionViewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsProductAddonsSectionViewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class FlowsSubmitRenewalOrderElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsSubmitRenewalSectionOrdersPost$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsFlowsSubmitRenewalSectionOrdersPost$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsSubmitRenewalOrderElementService.ElementsFlowsSubmitRenewalSectionOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsSubmitRenewalSectionOrdersPost$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsFlowsSubmitRenewalSectionOrdersPost$Json(params) {
        return this.elementsFlowsSubmitRenewalSectionOrdersPost$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsSubmitRenewalSectionOrdersPost$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsFlowsSubmitRenewalSectionOrdersPost$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, FlowsSubmitRenewalOrderElementService.ElementsFlowsSubmitRenewalSectionOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsSubmitRenewalSectionOrdersPost$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsFlowsSubmitRenewalSectionOrdersPost$CspReport(params) {
        return this.elementsFlowsSubmitRenewalSectionOrdersPost$CspReport$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsFlowsSubmitRenewalSectionOrdersPost
 */
FlowsSubmitRenewalOrderElementService.ElementsFlowsSubmitRenewalSectionOrdersPostPath = '/elements/flows-submit-renewal-section/orders';
FlowsSubmitRenewalOrderElementService.ɵfac = function FlowsSubmitRenewalOrderElementService_Factory(t) { return new (t || FlowsSubmitRenewalOrderElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
FlowsSubmitRenewalOrderElementService.ɵprov = ɵɵdefineInjectable({ token: FlowsSubmitRenewalOrderElementService, factory: FlowsSubmitRenewalOrderElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FlowsSubmitRenewalOrderElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class HistoricalSpotPriceService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsHistoricalSpotPricePrivateCustomerGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsHistoricalSpotPricePrivateCustomerGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, HistoricalSpotPriceService.ElementsHistoricalSpotPricePrivateCustomerGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsHistoricalSpotPricePrivateCustomerGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsHistoricalSpotPricePrivateCustomerGet(params) {
        return this.elementsHistoricalSpotPricePrivateCustomerGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsHistoricalSpotPriceCompanyCustomerGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsHistoricalSpotPriceCompanyCustomerGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, HistoricalSpotPriceService.ElementsHistoricalSpotPriceCompanyCustomerGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsHistoricalSpotPriceCompanyCustomerGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsHistoricalSpotPriceCompanyCustomerGet(params) {
        return this.elementsHistoricalSpotPriceCompanyCustomerGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsHistoricalSpotPricePrivateCustomerGet
 */
HistoricalSpotPriceService.ElementsHistoricalSpotPricePrivateCustomerGetPath = '/elements/historical-spot-price/private-customer';
/**
 * Path part for operation elementsHistoricalSpotPriceCompanyCustomerGet
 */
HistoricalSpotPriceService.ElementsHistoricalSpotPriceCompanyCustomerGetPath = '/elements/historical-spot-price/company-customer';
HistoricalSpotPriceService.ɵfac = function HistoricalSpotPriceService_Factory(t) { return new (t || HistoricalSpotPriceService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
HistoricalSpotPriceService.ɵprov = ɵɵdefineInjectable({ token: HistoricalSpotPriceService, factory: HistoricalSpotPriceService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(HistoricalSpotPriceService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyConsumptionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get electricity start date for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdElectricityStartDateGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdElectricityStartDateGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdElectricityStartDateGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('resolution', params.resolution, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: parseFloat(String(r.body)) });
        }));
    }
    /**
     * Get electricity start date for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdElectricityStartDateGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdElectricityStartDateGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdElectricityStartDateGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get electricity resolution history for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdElectricityHistoryGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdElectricityHistoryGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdElectricityHistoryGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('resolution', params.resolution, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get electricity resolution history for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdElectricityHistoryGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdElectricityHistoryGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdElectricityHistoryGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get trading fee start date for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: parseFloat(String(r.body)) });
        }));
    }
    /**
     * Get trading fee start date for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get heat start date for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdHeatStartDateGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHeatStartDateGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHeatStartDateGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: parseFloat(String(r.body)) });
        }));
    }
    /**
     * Get heat start date for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdHeatStartDateGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHeatStartDateGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdHeatStartDateGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get if the premise is consuming electricity or not (if not it is producing electricity).
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: String(r.body) === 'true' });
        }));
    }
    /**
     * Get if the premise is consuming electricity or not (if not it is producing electricity).
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the grid owner of the specified premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdGridOwnerGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdGridOwnerGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdGridOwnerGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the grid owner of the specified premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdGridOwnerGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdGridOwnerGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdGridOwnerGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get energy area of the specified premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdEnergyAreaGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdEnergyAreaGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdEnergyAreaGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get energy area of the specified premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdEnergyAreaGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdEnergyAreaGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdEnergyAreaGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get available resolutions for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdResolutionsGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdResolutionsGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdResolutionsGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get available resolutions for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdResolutionsGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdResolutionsGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdResolutionsGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get electricity consumption for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdConsumptionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdConsumptionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdConsumptionGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get electricity consumption for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdConsumptionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdConsumptionGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdConsumptionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get heat consumption for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdHeatConsumptionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHeatConsumptionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHeatConsumptionGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
            rb.query('consumptionType', params.consumptionType, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get heat consumption for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdHeatConsumptionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHeatConsumptionGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdHeatConsumptionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdHourlySpotPricesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHourlySpotPricesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHourlySpotPricesGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdHourlySpotPricesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHourlySpotPricesGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdHourlySpotPricesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get customer specific hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get customer specific hourly spot prices for energy area where premise is located.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get cost for consumption for a premise for a given resolution.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdCostGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdCostGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdCostGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
            rb.query('version', params.version, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get cost for consumption for a premise for a given resolution.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdCostGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdCostGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdCostGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get temperatures for a premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdTemperatureGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdTemperatureGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdTemperatureGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get temperatures for a premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdTemperatureGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdTemperatureGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdTemperatureGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get WAC(WeatherAdjustedConsumption-Normalårskorrigerad Förbrukning) Calculation for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
            rb.query('resolution', params.resolution, {});
            rb.query('consumptionType', params.consumptionType, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get WAC(WeatherAdjustedConsumption-Normalårskorrigerad Förbrukning) Calculation for a premise for a given resolution and consumption type.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get power of attorney status for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get power of attorney status for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGet(params) {
        return this.elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Start power of attorney process for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPut()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPut$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPutPath, 'put');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Start power of attorney process for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPut$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPut(params) {
        return this.elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPut$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdElectricityStartDateGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdElectricityStartDateGetPath = '/elements/my-consumption/premises/{premiseId}/electricity-start-date';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdElectricityHistoryGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdElectricityHistoryGetPath = '/elements/my-consumption/premises/{premiseId}/electricity-history';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdTradingFeeStartDateGetPath = '/elements/my-consumption/premises/{premiseId}/trading-fee-start-date';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdHeatStartDateGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHeatStartDateGetPath = '/elements/my-consumption/premises/{premiseId}/heat-start-date';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdIsConsumingElectricityGetPath = '/elements/my-consumption/premises/{premiseId}/is-consuming-electricity';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdGridOwnerGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdGridOwnerGetPath = '/elements/my-consumption/premises/{premiseId}/grid-owner';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdEnergyAreaGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdEnergyAreaGetPath = '/elements/my-consumption/premises/{premiseId}/energy-area';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdResolutionsGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdResolutionsGetPath = '/elements/my-consumption/premises/{premiseId}/resolutions';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdConsumptionGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdConsumptionGetPath = '/elements/my-consumption/premises/{premiseId}/consumption';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdHeatConsumptionGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHeatConsumptionGetPath = '/elements/my-consumption/premises/{premiseId}/heat-consumption';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdHourlySpotPricesGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHourlySpotPricesGetPath = '/elements/my-consumption/premises/{premiseId}/hourly-spot-prices';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdCustomerSpecificHourlySpotPricesGetPath = '/elements/my-consumption/premises/{premiseId}/customer-specific-hourly-spot-prices';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdCostGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdCostGetPath = '/elements/my-consumption/premises/{premiseId}/cost';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdTemperatureGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdTemperatureGetPath = '/elements/my-consumption/premises/{premiseId}/temperature';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdHeatWacConsumptionGetPath = '/elements/my-consumption/premises/{premiseId}/heat-wac-consumption';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGet
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdPowerOfAttorneyGetPath = '/elements/my-consumption/premises/{premiseId}/power-of-attorney';
/**
 * Path part for operation elementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPut
 */
MyConsumptionElementService.ElementsMyConsumptionPremisesPremiseIdPowerOfAttorneyPutPath = '/elements/my-consumption/premises/{premiseId}/power-of-attorney';
MyConsumptionElementService.ɵfac = function MyConsumptionElementService_Factory(t) { return new (t || MyConsumptionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyConsumptionElementService.ɵprov = ɵɵdefineInjectable({ token: MyConsumptionElementService, factory: MyConsumptionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyConsumptionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyConsumptionElementAnonymousService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get hourly spot prices for energy area.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyConsumptionElementAnonymousService.ElementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGetPath, 'get');
        if (params) {
            rb.path('energyArea', params.energyArea, {});
            rb.query('fromDate', params.fromDate, {});
            rb.query('toDate', params.toDate, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get hourly spot prices for energy area.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGet(params) {
        return this.elementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGet
 */
MyConsumptionElementAnonymousService.ElementsMyConsumptionEnergyAreaEnergyAreaHourlySpotPricesGetPath = '/elements/my-consumption/energy-area/{energyArea}/hourly-spot-prices';
MyConsumptionElementAnonymousService.ɵfac = function MyConsumptionElementAnonymousService_Factory(t) { return new (t || MyConsumptionElementAnonymousService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyConsumptionElementAnonymousService.ɵprov = ɵɵdefineInjectable({ token: MyConsumptionElementAnonymousService, factory: MyConsumptionElementAnonymousService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyConsumptionElementAnonymousService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyContactNotificationDialogueElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get contact information for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyContactNotificationDialogCustomerContactInformationGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContactNotificationDialogCustomerContactInformationGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyContactNotificationDialogueElementService.ElementsMyContactNotificationDialogCustomerContactInformationGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get contact information for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyContactNotificationDialogCustomerContactInformationGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContactNotificationDialogCustomerContactInformationGet(params) {
        return this.elementsMyContactNotificationDialogCustomerContactInformationGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information email and mobile number for the currently logged in user, without changing the notification flags or telephone number.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyContactNotificationDialogCustomerContactInformationPut()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsMyContactNotificationDialogCustomerContactInformationPut$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyContactNotificationDialogueElementService.ElementsMyContactNotificationDialogCustomerContactInformationPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information email and mobile number for the currently logged in user, without changing the notification flags or telephone number.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyContactNotificationDialogCustomerContactInformationPut$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsMyContactNotificationDialogCustomerContactInformationPut(params) {
        return this.elementsMyContactNotificationDialogCustomerContactInformationPut$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyContactNotificationDialogCustomerContactInformationGet
 */
MyContactNotificationDialogueElementService.ElementsMyContactNotificationDialogCustomerContactInformationGetPath = '/elements/my-contact-notification-dialog/customer/contact-information';
/**
 * Path part for operation elementsMyContactNotificationDialogCustomerContactInformationPut
 */
MyContactNotificationDialogueElementService.ElementsMyContactNotificationDialogCustomerContactInformationPutPath = '/elements/my-contact-notification-dialog/customer/contact-information';
MyContactNotificationDialogueElementService.ɵfac = function MyContactNotificationDialogueElementService_Factory(t) { return new (t || MyContactNotificationDialogueElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyContactNotificationDialogueElementService.ɵprov = ɵɵdefineInjectable({ token: MyContactNotificationDialogueElementService, factory: MyContactNotificationDialogueElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyContactNotificationDialogueElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyContractsElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the premise details for the specified premise id and premise type.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyContractsPremisesPremiseIdPremiseDetailsGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContractsPremisesPremiseIdPremiseDetailsGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyContractsElementService.ElementsMyContractsPremisesPremiseIdPremiseDetailsGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('premiseType', params.premiseType, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the premise details for the specified premise id and premise type.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyContractsPremisesPremiseIdPremiseDetailsGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContractsPremisesPremiseIdPremiseDetailsGet(params) {
        return this.elementsMyContractsPremisesPremiseIdPremiseDetailsGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get all contracts for the specified premise id and premise type.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyContractsPremisesPremiseIdContractsGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContractsPremisesPremiseIdContractsGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyContractsElementService.ElementsMyContractsPremisesPremiseIdContractsGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('premiseType', params.premiseType, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get all contracts for the specified premise id and premise type.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyContractsPremisesPremiseIdContractsGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContractsPremisesPremiseIdContractsGet(params) {
        return this.elementsMyContractsPremisesPremiseIdContractsGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyContractsPremisesPremiseIdPremiseDetailsGet
 */
MyContractsElementService.ElementsMyContractsPremisesPremiseIdPremiseDetailsGetPath = '/elements/my-contracts/premises/{premiseId}/premise-details';
/**
 * Path part for operation elementsMyContractsPremisesPremiseIdContractsGet
 */
MyContractsElementService.ElementsMyContractsPremisesPremiseIdContractsGetPath = '/elements/my-contracts/premises/{premiseId}/contracts';
MyContractsElementService.ɵfac = function MyContractsElementService_Factory(t) { return new (t || MyContractsElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyContractsElementService.ɵprov = ɵɵdefineInjectable({ token: MyContractsElementService, factory: MyContractsElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyContractsElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyContractsViewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the contracts for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyContractsViewPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContractsViewPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyContractsViewElementService.ElementsMyContractsViewPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('includeFutureContracts', params.includeFutureContracts, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the contracts for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyContractsViewPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyContractsViewPremisesPremiseIdGet(params) {
        return this.elementsMyContractsViewPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyContractsViewPremisesPremiseIdGet
 */
MyContractsViewElementService.ElementsMyContractsViewPremisesPremiseIdGetPath = '/elements/my-contracts-view/premises/{premiseId}';
MyContractsViewElementService.ɵfac = function MyContractsViewElementService_Factory(t) { return new (t || MyContractsViewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyContractsViewElementService.ɵprov = ɵɵdefineInjectable({ token: MyContractsViewElementService, factory: MyContractsViewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyContractsViewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyEnergyElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get measurement ranges for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyEnergyPremisesPremiseIdMeasurementRangesGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyEnergyPremisesPremiseIdMeasurementRangesGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyEnergyElementService.ElementsMyEnergyPremisesPremiseIdMeasurementRangesGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get measurement ranges for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyEnergyPremisesPremiseIdMeasurementRangesGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyEnergyPremisesPremiseIdMeasurementRangesGet(params) {
        return this.elementsMyEnergyPremisesPremiseIdMeasurementRangesGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get measurement for premise for specified resolution, measurementType and start and end dates.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyEnergyPremisesPremiseIdMeasurementGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyEnergyPremisesPremiseIdMeasurementGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyEnergyElementService.ElementsMyEnergyPremisesPremiseIdMeasurementGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('resolution', params.resolution, {});
            rb.query('measurementType', params.measurementType, {});
            rb.query('startDate', params.startDate, {});
            rb.query('endDate', params.endDate, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get measurement for premise for specified resolution, measurementType and start and end dates.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyEnergyPremisesPremiseIdMeasurementGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyEnergyPremisesPremiseIdMeasurementGet(params) {
        return this.elementsMyEnergyPremisesPremiseIdMeasurementGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyEnergyPremisesPremiseIdMeasurementRangesGet
 */
MyEnergyElementService.ElementsMyEnergyPremisesPremiseIdMeasurementRangesGetPath = '/elements/my-energy/premises/{premiseId}/measurement-ranges';
/**
 * Path part for operation elementsMyEnergyPremisesPremiseIdMeasurementGet
 */
MyEnergyElementService.ElementsMyEnergyPremisesPremiseIdMeasurementGetPath = '/elements/my-energy/premises/{premiseId}/measurement';
MyEnergyElementService.ɵfac = function MyEnergyElementService_Factory(t) { return new (t || MyEnergyElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyEnergyElementService.ɵprov = ɵɵdefineInjectable({ token: MyEnergyElementService, factory: MyEnergyElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyEnergyElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyInvoicesViewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the invoices for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyInvoicesViewPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyInvoicesViewPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyInvoicesViewElementService.ElementsMyInvoicesViewPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
            rb.query('fromEpochTime', params.fromEpochTime, {});
            rb.query('toEpochTime', params.toEpochTime, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the invoices for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyInvoicesViewPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyInvoicesViewPremisesPremiseIdGet(params) {
        return this.elementsMyInvoicesViewPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the invoice file content for the specified invoice id for the customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyInvoicesViewInvoicesInvoiceIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyInvoicesViewInvoicesInvoiceIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyInvoicesViewElementService.ElementsMyInvoicesViewInvoicesInvoiceIdGetPath, 'get');
        if (params) {
            rb.path('invoiceId', params.invoiceId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the invoice file content for the specified invoice id for the customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyInvoicesViewInvoicesInvoiceIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyInvoicesViewInvoicesInvoiceIdGet(params) {
        return this.elementsMyInvoicesViewInvoicesInvoiceIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyInvoicesViewPremisesPremiseIdGet
 */
MyInvoicesViewElementService.ElementsMyInvoicesViewPremisesPremiseIdGetPath = '/elements/my-invoices-view/premises/{premiseId}';
/**
 * Path part for operation elementsMyInvoicesViewInvoicesInvoiceIdGet
 */
MyInvoicesViewElementService.ElementsMyInvoicesViewInvoicesInvoiceIdGetPath = '/elements/my-invoices-view/invoices/{invoiceId}';
MyInvoicesViewElementService.ɵfac = function MyInvoicesViewElementService_Factory(t) { return new (t || MyInvoicesViewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyInvoicesViewElementService.ɵprov = ɵɵdefineInjectable({ token: MyInvoicesViewElementService, factory: MyInvoicesViewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyInvoicesViewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyPagesLoginPageService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the my pages login customer view model for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `myPagesLoginPageCustomerGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    myPagesLoginPageCustomerGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyPagesLoginPageService.MyPagesLoginPageCustomerGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the my pages login customer view model for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `myPagesLoginPageCustomerGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    myPagesLoginPageCustomerGet(params) {
        return this.myPagesLoginPageCustomerGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation myPagesLoginPageCustomerGet
 */
MyPagesLoginPageService.MyPagesLoginPageCustomerGetPath = '/my-pages-login-page/customer';
MyPagesLoginPageService.ɵfac = function MyPagesLoginPageService_Factory(t) { return new (t || MyPagesLoginPageService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyPagesLoginPageService.ɵprov = ɵɵdefineInjectable({ token: MyPagesLoginPageService, factory: MyPagesLoginPageService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyPagesLoginPageService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyPoaConsumptionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get power of attorney status for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyPoaConsumptionElementService.ElementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get power of attorney status for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGet(params) {
        return this.elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Start power of attorney process for premise.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPut()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPut$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyPoaConsumptionElementService.ElementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPutPath, 'put');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Start power of attorney process for premise.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPut$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPut(params) {
        return this.elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPut$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGet
 */
MyPoaConsumptionElementService.ElementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyGetPath = '/elements/my-poa-consumption/premises/{premiseId}/power-of-attorney';
/**
 * Path part for operation elementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPut
 */
MyPoaConsumptionElementService.ElementsMyPoaConsumptionPremisesPremiseIdPowerOfAttorneyPutPath = '/elements/my-poa-consumption/premises/{premiseId}/power-of-attorney';
MyPoaConsumptionElementService.ɵfac = function MyPoaConsumptionElementService_Factory(t) { return new (t || MyPoaConsumptionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyPoaConsumptionElementService.ɵprov = ɵɵdefineInjectable({ token: MyPoaConsumptionElementService, factory: MyPoaConsumptionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyPoaConsumptionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyPremisesSelectElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get all premises associated with the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyPremisesSelectGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyPremisesSelectGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyPremisesSelectElementService.ElementsMyPremisesSelectGetPath, 'get');
        if (params) {
            rb.query('includeFutureContracts', params.includeFutureContracts, {});
            rb.query('contractTypeFilter', params.contractTypeFilter, {});
            rb.query('excludeContractTypes', params.excludeContractTypes, {});
            rb.query('combineMicroProductionPremises', params.combineMicroProductionPremises, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get all premises associated with the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyPremisesSelectGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyPremisesSelectGet(params) {
        return this.elementsMyPremisesSelectGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyPremisesSelectGet
 */
MyPremisesSelectElementService.ElementsMyPremisesSelectGetPath = '/elements/my-premises-select';
MyPremisesSelectElementService.ɵfac = function MyPremisesSelectElementService_Factory(t) { return new (t || MyPremisesSelectElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyPremisesSelectElementService.ɵprov = ɵɵdefineInjectable({ token: MyPremisesSelectElementService, factory: MyPremisesSelectElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyPremisesSelectElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyProfileCommunicationSettingsViewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get communication communication settings for the logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileCommunicationSettingsViewElementService.ElementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get communication communication settings for the logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGet(params) {
        return this.elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update communication settings for the logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPut()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPut$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileCommunicationSettingsViewElementService.ElementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update communication settings for the logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPut$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPut(params) {
        return this.elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPut$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGet
 */
MyProfileCommunicationSettingsViewElementService.ElementsMyProfileCommunicationSettingsCustomerCommunicationSettingsGetPath = '/elements/my-profile-communication-settings/customer/communication-settings';
/**
 * Path part for operation elementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPut
 */
MyProfileCommunicationSettingsViewElementService.ElementsMyProfileCommunicationSettingsCustomerCommunicationSettingsPutPath = '/elements/my-profile-communication-settings/customer/communication-settings';
MyProfileCommunicationSettingsViewElementService.ɵfac = function MyProfileCommunicationSettingsViewElementService_Factory(t) { return new (t || MyProfileCommunicationSettingsViewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyProfileCommunicationSettingsViewElementService.ɵprov = ɵɵdefineInjectable({ token: MyProfileCommunicationSettingsViewElementService, factory: MyProfileCommunicationSettingsViewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyProfileCommunicationSettingsViewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyProfileDetailsViewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get profile information for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileDetailsViewCustomerProfileInformationGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileDetailsViewCustomerProfileInformationGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileDetailsViewElementService.ElementsMyProfileDetailsViewCustomerProfileInformationGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get profile information for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileDetailsViewCustomerProfileInformationGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileDetailsViewCustomerProfileInformationGet(params) {
        return this.elementsMyProfileDetailsViewCustomerProfileInformationGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update profile information phone number for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileDetailsViewCustomerProfileInformationPatch()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsMyProfileDetailsViewCustomerProfileInformationPatch$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileDetailsViewElementService.ElementsMyProfileDetailsViewCustomerProfileInformationPatchPath, 'patch');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update profile information phone number for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileDetailsViewCustomerProfileInformationPatch$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsMyProfileDetailsViewCustomerProfileInformationPatch(params) {
        return this.elementsMyProfileDetailsViewCustomerProfileInformationPatch$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyProfileDetailsViewCustomerProfileInformationGet
 */
MyProfileDetailsViewElementService.ElementsMyProfileDetailsViewCustomerProfileInformationGetPath = '/elements/my-profile-details-view/customer/profile-information';
/**
 * Path part for operation elementsMyProfileDetailsViewCustomerProfileInformationPatch
 */
MyProfileDetailsViewElementService.ElementsMyProfileDetailsViewCustomerProfileInformationPatchPath = '/elements/my-profile-details-view/customer/profile-information';
MyProfileDetailsViewElementService.ɵfac = function MyProfileDetailsViewElementService_Factory(t) { return new (t || MyProfileDetailsViewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyProfileDetailsViewElementService.ɵprov = ɵɵdefineInjectable({ token: MyProfileDetailsViewElementService, factory: MyProfileDetailsViewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyProfileDetailsViewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyProfileElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get contact information for the logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileContactDetailsGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get contact information for the logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileContactDetailsGet(params) {
        return this.elementsMyProfileContactDetailsGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information email address for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsEmailEmailAddressPut$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsEmailEmailAddressPut$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsEmailEmailAddressPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information email address for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsEmailEmailAddressPut$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsEmailEmailAddressPut$Json(params) {
        return this.elementsMyProfileContactDetailsEmailEmailAddressPut$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information email address for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsEmailEmailAddressPut$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsEmailEmailAddressPut$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsEmailEmailAddressPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information email address for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsEmailEmailAddressPut$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsEmailEmailAddressPut$CspReport(params) {
        return this.elementsMyProfileContactDetailsEmailEmailAddressPut$CspReport$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information email subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsEmailSubscribePut$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsEmailSubscribePut$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsEmailSubscribePutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information email subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsEmailSubscribePut$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsEmailSubscribePut$Json(params) {
        return this.elementsMyProfileContactDetailsEmailSubscribePut$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information email subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsEmailSubscribePut$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsEmailSubscribePut$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsEmailSubscribePutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information email subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsEmailSubscribePut$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsEmailSubscribePut$CspReport(params) {
        return this.elementsMyProfileContactDetailsEmailSubscribePut$CspReport$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information mobile number for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsMobileNumberPut$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsMobileNumberPut$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsMobileNumberPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information mobile number for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsMobileNumberPut$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsMobileNumberPut$Json(params) {
        return this.elementsMyProfileContactDetailsMobileNumberPut$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update contact information mobile number for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsMobileNumberPut$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsMobileNumberPut$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsMobileNumberPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update contact information mobile number for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsMobileNumberPut$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsMobileNumberPut$CspReport(params) {
        return this.elementsMyProfileContactDetailsMobileNumberPut$CspReport$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update profile information mobile subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsMobileSubscribePut$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsMobileSubscribePut$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsMobileSubscribePutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update profile information mobile subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsMobileSubscribePut$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileContactDetailsMobileSubscribePut$Json(params) {
        return this.elementsMyProfileContactDetailsMobileSubscribePut$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update profile information mobile subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileContactDetailsMobileSubscribePut$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsMobileSubscribePut$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileContactDetailsMobileSubscribePutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update profile information mobile subscription for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileContactDetailsMobileSubscribePut$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileContactDetailsMobileSubscribePut$CspReport(params) {
        return this.elementsMyProfileContactDetailsMobileSubscribePut$CspReport$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get invoice settings information for a premise for the logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileInvoiceSettingsGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsGetPath, 'get');
        if (params) {
            rb.query('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get invoice settings information for a premise for the logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileInvoiceSettingsGet(params) {
        return this.elementsMyProfileInvoiceSettingsGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update invoice address for the currently logged in user either for a specific premise or for all premises.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsPost$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileInvoiceSettingsPost$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update invoice address for the currently logged in user either for a specific premise or for all premises.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsPost$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileInvoiceSettingsPost$Json(params) {
        return this.elementsMyProfileInvoiceSettingsPost$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update invoice address for the currently logged in user either for a specific premise or for all premises.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsPost$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileInvoiceSettingsPost$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update invoice address for the currently logged in user either for a specific premise or for all premises.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsPost$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileInvoiceSettingsPost$CspReport(params) {
        return this.elementsMyProfileInvoiceSettingsPost$CspReport$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the main address for the logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsMainAddressGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileInvoiceSettingsMainAddressGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsMainAddressGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the main address for the logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsMainAddressGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileInvoiceSettingsMainAddressGet(params) {
        return this.elementsMyProfileInvoiceSettingsMainAddressGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update invoice interval for all premises for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsInvoiceIntervalPut$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileInvoiceSettingsInvoiceIntervalPut$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsInvoiceIntervalPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update invoice interval for all premises for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsInvoiceIntervalPut$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileInvoiceSettingsInvoiceIntervalPut$Json(params) {
        return this.elementsMyProfileInvoiceSettingsInvoiceIntervalPut$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update invoice interval for all premises for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsInvoiceIntervalPut$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileInvoiceSettingsInvoiceIntervalPut$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsInvoiceIntervalPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Update invoice interval for all premises for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsInvoiceIntervalPut$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileInvoiceSettingsInvoiceIntervalPut$CspReport(params) {
        return this.elementsMyProfileInvoiceSettingsInvoiceIntervalPut$CspReport$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get available banks that support e-invoice as payment method.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsPaymentBanksGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileInvoiceSettingsPaymentBanksGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsPaymentBanksGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get available banks that support e-invoice as payment method.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsPaymentBanksGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyProfileInvoiceSettingsPaymentBanksGet(params) {
        return this.elementsMyProfileInvoiceSettingsPaymentBanksGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Register e-invoice to a bank.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Register e-invoice to a bank.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$Json(params) {
        return this.elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Register e-invoice to a bank.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyProfileElementService.ElementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Register e-invoice to a bank.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$CspReport(params) {
        return this.elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost$CspReport$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyProfileContactDetailsGet
 */
MyProfileElementService.ElementsMyProfileContactDetailsGetPath = '/elements/my-profile/contact-details';
/**
 * Path part for operation elementsMyProfileContactDetailsEmailEmailAddressPut
 */
MyProfileElementService.ElementsMyProfileContactDetailsEmailEmailAddressPutPath = '/elements/my-profile/contact-details/email/email-address';
/**
 * Path part for operation elementsMyProfileContactDetailsEmailSubscribePut
 */
MyProfileElementService.ElementsMyProfileContactDetailsEmailSubscribePutPath = '/elements/my-profile/contact-details/email/subscribe';
/**
 * Path part for operation elementsMyProfileContactDetailsMobileNumberPut
 */
MyProfileElementService.ElementsMyProfileContactDetailsMobileNumberPutPath = '/elements/my-profile/contact-details/mobile/number';
/**
 * Path part for operation elementsMyProfileContactDetailsMobileSubscribePut
 */
MyProfileElementService.ElementsMyProfileContactDetailsMobileSubscribePutPath = '/elements/my-profile/contact-details/mobile/subscribe';
/**
 * Path part for operation elementsMyProfileInvoiceSettingsGet
 */
MyProfileElementService.ElementsMyProfileInvoiceSettingsGetPath = '/elements/my-profile/invoice-settings';
/**
 * Path part for operation elementsMyProfileInvoiceSettingsPost
 */
MyProfileElementService.ElementsMyProfileInvoiceSettingsPostPath = '/elements/my-profile/invoice-settings';
/**
 * Path part for operation elementsMyProfileInvoiceSettingsMainAddressGet
 */
MyProfileElementService.ElementsMyProfileInvoiceSettingsMainAddressGetPath = '/elements/my-profile/invoice-settings/main-address';
/**
 * Path part for operation elementsMyProfileInvoiceSettingsInvoiceIntervalPut
 */
MyProfileElementService.ElementsMyProfileInvoiceSettingsInvoiceIntervalPutPath = '/elements/my-profile/invoice-settings/invoice-interval';
/**
 * Path part for operation elementsMyProfileInvoiceSettingsPaymentBanksGet
 */
MyProfileElementService.ElementsMyProfileInvoiceSettingsPaymentBanksGetPath = '/elements/my-profile/invoice-settings/payment/banks';
/**
 * Path part for operation elementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePost
 */
MyProfileElementService.ElementsMyProfileInvoiceSettingsPaymentRegisterEinvoicePostPath = '/elements/my-profile/invoice-settings/payment/register/einvoice';
MyProfileElementService.ɵfac = function MyProfileElementService_Factory(t) { return new (t || MyProfileElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyProfileElementService.ɵprov = ɵɵdefineInjectable({ token: MyProfileElementService, factory: MyProfileElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyProfileElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyWidgetsConsumptionOverviewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the consumption overview widget for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyWidgetsConsumptionOverviewPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsConsumptionOverviewPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyWidgetsConsumptionOverviewElementService.ElementsMyWidgetsConsumptionOverviewPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the consumption overview widget for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyWidgetsConsumptionOverviewPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsConsumptionOverviewPremisesPremiseIdGet(params) {
        return this.elementsMyWidgetsConsumptionOverviewPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyWidgetsConsumptionOverviewPremisesPremiseIdGet
 */
MyWidgetsConsumptionOverviewElementService.ElementsMyWidgetsConsumptionOverviewPremisesPremiseIdGetPath = '/elements/my-widgets-consumption-overview/premises/{premiseId}';
MyWidgetsConsumptionOverviewElementService.ɵfac = function MyWidgetsConsumptionOverviewElementService_Factory(t) { return new (t || MyWidgetsConsumptionOverviewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyWidgetsConsumptionOverviewElementService.ɵprov = ɵɵdefineInjectable({ token: MyWidgetsConsumptionOverviewElementService, factory: MyWidgetsConsumptionOverviewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyWidgetsConsumptionOverviewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyWidgetsContractPriceElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the contract widgets for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyWidgetsContractPricePremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsContractPricePremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyWidgetsContractPriceElementService.ElementsMyWidgetsContractPricePremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the contract widgets for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyWidgetsContractPricePremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsContractPricePremisesPremiseIdGet(params) {
        return this.elementsMyWidgetsContractPricePremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyWidgetsContractPricePremisesPremiseIdGet
 */
MyWidgetsContractPriceElementService.ElementsMyWidgetsContractPricePremisesPremiseIdGetPath = '/elements/my-widgets-contract-price/premises/{premiseId}';
MyWidgetsContractPriceElementService.ɵfac = function MyWidgetsContractPriceElementService_Factory(t) { return new (t || MyWidgetsContractPriceElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyWidgetsContractPriceElementService.ɵprov = ɵɵdefineInjectable({ token: MyWidgetsContractPriceElementService, factory: MyWidgetsContractPriceElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyWidgetsContractPriceElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyWidgetsElectricityPriceNowElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the current hourly price for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyWidgetsElectricityPriceNowPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsElectricityPriceNowPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyWidgetsElectricityPriceNowElementService.ElementsMyWidgetsElectricityPriceNowPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the current hourly price for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyWidgetsElectricityPriceNowPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsElectricityPriceNowPremisesPremiseIdGet(params) {
        return this.elementsMyWidgetsElectricityPriceNowPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyWidgetsElectricityPriceNowPremisesPremiseIdGet
 */
MyWidgetsElectricityPriceNowElementService.ElementsMyWidgetsElectricityPriceNowPremisesPremiseIdGetPath = '/elements/my-widgets-electricity-price-now/premises/{premiseId}';
MyWidgetsElectricityPriceNowElementService.ɵfac = function MyWidgetsElectricityPriceNowElementService_Factory(t) { return new (t || MyWidgetsElectricityPriceNowElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyWidgetsElectricityPriceNowElementService.ɵprov = ɵɵdefineInjectable({ token: MyWidgetsElectricityPriceNowElementService, factory: MyWidgetsElectricityPriceNowElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyWidgetsElectricityPriceNowElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyWidgetsInvoiceOverviewElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the invoice overview for the latest invoice for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyWidgetsInvoiceOverviewPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsInvoiceOverviewPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyWidgetsInvoiceOverviewElementService.ElementsMyWidgetsInvoiceOverviewPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the invoice overview for the latest invoice for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyWidgetsInvoiceOverviewPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsInvoiceOverviewPremisesPremiseIdGet(params) {
        return this.elementsMyWidgetsInvoiceOverviewPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyWidgetsInvoiceOverviewPremisesPremiseIdGet
 */
MyWidgetsInvoiceOverviewElementService.ElementsMyWidgetsInvoiceOverviewPremisesPremiseIdGetPath = '/elements/my-widgets-invoice-overview/premises/{premiseId}';
MyWidgetsInvoiceOverviewElementService.ɵfac = function MyWidgetsInvoiceOverviewElementService_Factory(t) { return new (t || MyWidgetsInvoiceOverviewElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyWidgetsInvoiceOverviewElementService.ɵprov = ɵɵdefineInjectable({ token: MyWidgetsInvoiceOverviewElementService, factory: MyWidgetsInvoiceOverviewElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyWidgetsInvoiceOverviewElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class MyWidgetsPremiseDetailsElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the premise details widget for the specified premise id.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsMyWidgetsPremiseDetailsPremisesPremiseIdGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsPremiseDetailsPremisesPremiseIdGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, MyWidgetsPremiseDetailsElementService.ElementsMyWidgetsPremiseDetailsPremisesPremiseIdGetPath, 'get');
        if (params) {
            rb.path('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the premise details widget for the specified premise id.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsMyWidgetsPremiseDetailsPremisesPremiseIdGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsMyWidgetsPremiseDetailsPremisesPremiseIdGet(params) {
        return this.elementsMyWidgetsPremiseDetailsPremisesPremiseIdGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsMyWidgetsPremiseDetailsPremisesPremiseIdGet
 */
MyWidgetsPremiseDetailsElementService.ElementsMyWidgetsPremiseDetailsPremisesPremiseIdGetPath = '/elements/my-widgets-premise-details/premises/{premiseId}';
MyWidgetsPremiseDetailsElementService.ɵfac = function MyWidgetsPremiseDetailsElementService_Factory(t) { return new (t || MyWidgetsPremiseDetailsElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
MyWidgetsPremiseDetailsElementService.ɵprov = ɵɵdefineInjectable({ token: MyWidgetsPremiseDetailsElementService, factory: MyWidgetsPremiseDetailsElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MyWidgetsPremiseDetailsElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class OutageSubscriptionDialogElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get outage information for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsOutageSubscriptionDialogCustomerOutageInformationGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsOutageSubscriptionDialogCustomerOutageInformationGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, OutageSubscriptionDialogElementService.ElementsOutageSubscriptionDialogCustomerOutageInformationGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get outage information for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsOutageSubscriptionDialogCustomerOutageInformationGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsOutageSubscriptionDialogCustomerOutageInformationGet(params) {
        return this.elementsOutageSubscriptionDialogCustomerOutageInformationGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update outage information email and phone number for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsOutageSubscriptionDialogCustomerOutageInformationPut$Json()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsOutageSubscriptionDialogCustomerOutageInformationPut$Json$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, OutageSubscriptionDialogElementService.ElementsOutageSubscriptionDialogCustomerOutageInformationPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/*+json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: String(r.body) === 'true' });
        }));
    }
    /**
     * Update outage information email and phone number for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsOutageSubscriptionDialogCustomerOutageInformationPut$Json$Response()` instead.
     *
     * This method sends `application/*+json` and handles request body of type `application/*+json`.
     */
    elementsOutageSubscriptionDialogCustomerOutageInformationPut$Json(params) {
        return this.elementsOutageSubscriptionDialogCustomerOutageInformationPut$Json$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Update outage information email and phone number for the currently logged in user.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsOutageSubscriptionDialogCustomerOutageInformationPut$CspReport()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsOutageSubscriptionDialogCustomerOutageInformationPut$CspReport$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, OutageSubscriptionDialogElementService.ElementsOutageSubscriptionDialogCustomerOutageInformationPutPath, 'put');
        if (params) {
            rb.body(params.body, 'application/csp-report');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r.clone({ body: String(r.body) === 'true' });
        }));
    }
    /**
     * Update outage information email and phone number for the currently logged in user.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsOutageSubscriptionDialogCustomerOutageInformationPut$CspReport$Response()` instead.
     *
     * This method sends `application/csp-report` and handles request body of type `application/csp-report`.
     */
    elementsOutageSubscriptionDialogCustomerOutageInformationPut$CspReport(params) {
        return this.elementsOutageSubscriptionDialogCustomerOutageInformationPut$CspReport$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsOutageSubscriptionDialogCustomerOutageInformationGet
 */
OutageSubscriptionDialogElementService.ElementsOutageSubscriptionDialogCustomerOutageInformationGetPath = '/elements/outage-subscription-dialog/customer/outage-information';
/**
 * Path part for operation elementsOutageSubscriptionDialogCustomerOutageInformationPut
 */
OutageSubscriptionDialogElementService.ElementsOutageSubscriptionDialogCustomerOutageInformationPutPath = '/elements/outage-subscription-dialog/customer/outage-information';
OutageSubscriptionDialogElementService.ɵfac = function OutageSubscriptionDialogElementService_Factory(t) { return new (t || OutageSubscriptionDialogElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
OutageSubscriptionDialogElementService.ɵprov = ɵɵdefineInjectable({ token: OutageSubscriptionDialogElementService, factory: OutageSubscriptionDialogElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(OutageSubscriptionDialogElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class PostalCodeGridAreaElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the postal code grid area element view model for the specified postal code.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsPostalCodeGridAreaPostalCodesPostalCodeGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsPostalCodeGridAreaPostalCodesPostalCodeGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, PostalCodeGridAreaElementService.ElementsPostalCodeGridAreaPostalCodesPostalCodeGetPath, 'get');
        if (params) {
            rb.path('postalCode', params.postalCode, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the postal code grid area element view model for the specified postal code.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsPostalCodeGridAreaPostalCodesPostalCodeGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsPostalCodeGridAreaPostalCodesPostalCodeGet(params) {
        return this.elementsPostalCodeGridAreaPostalCodesPostalCodeGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the postal code grid area element view model for the specified grid area.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsPostalCodeGridAreaGridAreasGridAreaGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsPostalCodeGridAreaGridAreasGridAreaGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, PostalCodeGridAreaElementService.ElementsPostalCodeGridAreaGridAreasGridAreaGetPath, 'get');
        if (params) {
            rb.path('gridArea', params.gridArea, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the postal code grid area element view model for the specified grid area.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsPostalCodeGridAreaGridAreasGridAreaGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    elementsPostalCodeGridAreaGridAreasGridAreaGet(params) {
        return this.elementsPostalCodeGridAreaGridAreasGridAreaGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation elementsPostalCodeGridAreaPostalCodesPostalCodeGet
 */
PostalCodeGridAreaElementService.ElementsPostalCodeGridAreaPostalCodesPostalCodeGetPath = '/elements/postal-code-grid-area/postal-codes/{postalCode}';
/**
 * Path part for operation elementsPostalCodeGridAreaGridAreasGridAreaGet
 */
PostalCodeGridAreaElementService.ElementsPostalCodeGridAreaGridAreasGridAreaGetPath = '/elements/postal-code-grid-area/grid-areas/{gridArea}';
PostalCodeGridAreaElementService.ɵfac = function PostalCodeGridAreaElementService_Factory(t) { return new (t || PostalCodeGridAreaElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
PostalCodeGridAreaElementService.ɵprov = ɵɵdefineInjectable({ token: PostalCodeGridAreaElementService, factory: PostalCodeGridAreaElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PostalCodeGridAreaElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class RenewalFlowsCheckoutSectionService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the summary for a specific premiseId and selected productId and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getSummary()` instead.
     *
     * This method doesn't expect any request body.
     */
    getSummary$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, RenewalFlowsCheckoutSectionService.GetSummaryPath, 'get');
        if (params) {
            rb.query('premiseId', params.premiseId, {});
            rb.query('productId', params.productId, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the summary for a specific premiseId and selected productId and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getSummary$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getSummary(params) {
        return this.getSummary$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Submit an order to the order handler and send a confirmation email.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `elementsFlowsCheckoutSectionRenewalOrdersPost()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsCheckoutSectionRenewalOrdersPost$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, RenewalFlowsCheckoutSectionService.ElementsFlowsCheckoutSectionRenewalOrdersPostPath, 'post');
        if (params) {
            rb.body(params.body, 'application/json');
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Submit an order to the order handler and send a confirmation email.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `elementsFlowsCheckoutSectionRenewalOrdersPost$Response()` instead.
     *
     * This method sends `application/json` and handles request body of type `application/json`.
     */
    elementsFlowsCheckoutSectionRenewalOrdersPost(params) {
        return this.elementsFlowsCheckoutSectionRenewalOrdersPost$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getSummary
 */
RenewalFlowsCheckoutSectionService.GetSummaryPath = '/elements/flows-checkout-section/renewal/summary';
/**
 * Path part for operation elementsFlowsCheckoutSectionRenewalOrdersPost
 */
RenewalFlowsCheckoutSectionService.ElementsFlowsCheckoutSectionRenewalOrdersPostPath = '/elements/flows-checkout-section/renewal/orders';
RenewalFlowsCheckoutSectionService.ɵfac = function RenewalFlowsCheckoutSectionService_Factory(t) { return new (t || RenewalFlowsCheckoutSectionService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
RenewalFlowsCheckoutSectionService.ɵprov = ɵɵdefineInjectable({ token: RenewalFlowsCheckoutSectionService, factory: RenewalFlowsCheckoutSectionService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(RenewalFlowsCheckoutSectionService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class RenewalFlowsEnergySourcesSelectElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get a list of available energy sources with additional cost related to each energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getRenewalEnergySources()` instead.
     *
     * This method doesn't expect any request body.
     */
    getRenewalEnergySources$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, RenewalFlowsEnergySourcesSelectElementService.GetRenewalEnergySourcesPath, 'get');
        if (params) {
            rb.query('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get a list of available energy sources with additional cost related to each energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getRenewalEnergySources$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getRenewalEnergySources(params) {
        return this.getRenewalEnergySources$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getRenewalEnergySources
 */
RenewalFlowsEnergySourcesSelectElementService.GetRenewalEnergySourcesPath = '/elements/flows-energy-source-select-renewal-section';
RenewalFlowsEnergySourcesSelectElementService.ɵfac = function RenewalFlowsEnergySourcesSelectElementService_Factory(t) { return new (t || RenewalFlowsEnergySourcesSelectElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
RenewalFlowsEnergySourcesSelectElementService.ɵprov = ɵɵdefineInjectable({ token: RenewalFlowsEnergySourcesSelectElementService, factory: RenewalFlowsEnergySourcesSelectElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(RenewalFlowsEnergySourcesSelectElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class RenewalFlowsProductSelectOptionElementService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the product for a specific productId, premiseId and energy source.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getRenewableProductForEnergySource()` instead.
     *
     * This method doesn't expect any request body.
     */
    getRenewableProductForEnergySource$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, RenewalFlowsProductSelectOptionElementService.GetRenewableProductForEnergySourcePath, 'get');
        if (params) {
            rb.path('productId', params.productId, {});
            rb.query('premiseId', params.premiseId, {});
            rb.query('energySource', params.energySource, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the product for a specific productId, premiseId and energy source.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getRenewableProductForEnergySource$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getRenewableProductForEnergySource(params) {
        return this.getRenewableProductForEnergySource$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get available products for a specific premiseId in renewal.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `getRenewableProducts()` instead.
     *
     * This method doesn't expect any request body.
     */
    getRenewableProducts$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, RenewalFlowsProductSelectOptionElementService.GetRenewableProductsPath, 'get');
        if (params) {
            rb.query('premiseId', params.premiseId, {});
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get available products for a specific premiseId in renewal.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `getRenewableProducts$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    getRenewableProducts(params) {
        return this.getRenewableProducts$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation getRenewableProductForEnergySource
 */
RenewalFlowsProductSelectOptionElementService.GetRenewableProductForEnergySourcePath = '/elements/flows-product-select-option/renewal/products/{productId}';
/**
 * Path part for operation getRenewableProducts
 */
RenewalFlowsProductSelectOptionElementService.GetRenewableProductsPath = '/elements/flows-product-select-option/renewal/products';
RenewalFlowsProductSelectOptionElementService.ɵfac = function RenewalFlowsProductSelectOptionElementService_Factory(t) { return new (t || RenewalFlowsProductSelectOptionElementService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
RenewalFlowsProductSelectOptionElementService.ɵprov = ɵɵdefineInjectable({ token: RenewalFlowsProductSelectOptionElementService, factory: RenewalFlowsProductSelectOptionElementService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(RenewalFlowsProductSelectOptionElementService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/* tslint:disable */
class VisitorGroupCriteriaService extends BaseService {
    constructor(config, http) {
        super(config, http);
    }
    /**
     * Get the view models for the renewal status criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaRenewalStatusCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaRenewalStatusCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaRenewalStatusCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view models for the renewal status criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaRenewalStatusCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaRenewalStatusCriterionGet(params) {
        return this.visitorGroupCriteriaRenewalStatusCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get a boolean product change status criterion for the current customer. The boolean is true if any premise is eligible for product change.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaProductChangeStatusCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaProductChangeStatusCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaProductChangeStatusCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get a boolean product change status criterion for the current customer. The boolean is true if any premise is eligible for product change.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaProductChangeStatusCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaProductChangeStatusCriterionGet(params) {
        return this.visitorGroupCriteriaProductChangeStatusCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the view models for the service group criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaServiceGroupCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaServiceGroupCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaServiceGroupCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view models for the service group criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaServiceGroupCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaServiceGroupCriterionGet(params) {
        return this.visitorGroupCriteriaServiceGroupCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the view models for the service group future contract criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaServiceGroupFutureContractCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaServiceGroupFutureContractCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaServiceGroupFutureContractCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view models for the service group future contract criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaServiceGroupFutureContractCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaServiceGroupFutureContractCriterionGet(params) {
        return this.visitorGroupCriteriaServiceGroupFutureContractCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the view model for the winter assurance status criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaWinterAssuranceStatusCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaWinterAssuranceStatusCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaWinterAssuranceStatusCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view model for the winter assurance status criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaWinterAssuranceStatusCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaWinterAssuranceStatusCriterionGet(params) {
        return this.visitorGroupCriteriaWinterAssuranceStatusCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the view model for the invoice delivery method criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaInvoiceDeliveryMethodCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaInvoiceDeliveryMethodCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaInvoiceDeliveryMethodCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view model for the invoice delivery method criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaInvoiceDeliveryMethodCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaInvoiceDeliveryMethodCriterionGet(params) {
        return this.visitorGroupCriteriaInvoiceDeliveryMethodCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the view models for the contract type criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaContractTypeCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaContractTypeCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaContractTypeCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view models for the contract type criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaContractTypeCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaContractTypeCriterionGet(params) {
        return this.visitorGroupCriteriaContractTypeCriterionGet$Response(params).pipe(map((r) => r.body));
    }
    /**
     * Get the view models for the consumption power of attorney criterion for the current customer.
     *
     *
     *
     * This method provides access to the full `HttpResponse`, allowing access to response headers.
     * To access only the response body, use `visitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGet()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGet$Response(params) {
        const rb = new RequestBuilder(this.rootUrl, VisitorGroupCriteriaService.VisitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGetPath, 'get');
        if (params) {
        }
        return this.http.request(rb.build({
            responseType: 'json',
            accept: 'application/json'
        })).pipe(filter((r) => r instanceof HttpResponse), map((r) => {
            return r;
        }));
    }
    /**
     * Get the view models for the consumption power of attorney criterion for the current customer.
     *
     *
     *
     * This method provides access to only to the response body.
     * To access the full response (for headers, for example), `visitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGet$Response()` instead.
     *
     * This method doesn't expect any request body.
     */
    visitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGet(params) {
        return this.visitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGet$Response(params).pipe(map((r) => r.body));
    }
}
/**
 * Path part for operation visitorGroupCriteriaRenewalStatusCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaRenewalStatusCriterionGetPath = '/visitor-group-criteria/renewal-status-criterion';
/**
 * Path part for operation visitorGroupCriteriaProductChangeStatusCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaProductChangeStatusCriterionGetPath = '/visitor-group-criteria/product-change-status-criterion';
/**
 * Path part for operation visitorGroupCriteriaServiceGroupCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaServiceGroupCriterionGetPath = '/visitor-group-criteria/service-group-criterion';
/**
 * Path part for operation visitorGroupCriteriaServiceGroupFutureContractCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaServiceGroupFutureContractCriterionGetPath = '/visitor-group-criteria/service-group-future-contract-criterion';
/**
 * Path part for operation visitorGroupCriteriaWinterAssuranceStatusCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaWinterAssuranceStatusCriterionGetPath = '/visitor-group-criteria/winter-assurance-status-criterion';
/**
 * Path part for operation visitorGroupCriteriaInvoiceDeliveryMethodCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaInvoiceDeliveryMethodCriterionGetPath = '/visitor-group-criteria/invoice-delivery-method-criterion';
/**
 * Path part for operation visitorGroupCriteriaContractTypeCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaContractTypeCriterionGetPath = '/visitor-group-criteria/contract-type-criterion';
/**
 * Path part for operation visitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGet
 */
VisitorGroupCriteriaService.VisitorGroupCriteriaConsumptionPowerOfAttorneyCriterionGetPath = '/visitor-group-criteria/consumption-power-of-attorney-criterion';
VisitorGroupCriteriaService.ɵfac = function VisitorGroupCriteriaService_Factory(t) { return new (t || VisitorGroupCriteriaService)(ɵɵinject(ApiConfiguration), ɵɵinject(HttpClient)); };
VisitorGroupCriteriaService.ɵprov = ɵɵdefineInjectable({ token: VisitorGroupCriteriaService, factory: VisitorGroupCriteriaService.ɵfac, providedIn: 'root' });
/*@__PURE__*/ (function () { ɵsetClassMetadata(VisitorGroupCriteriaService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: ApiConfiguration }, { type: HttpClient }]; }, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { AcquisitionFlowsEnergySourcesSelectElementService, AcquisitionFlowsProductSelectOptionElementService, AssentlyConsumptionPowerOfAttorneyCallbackService, AverageSpotPriceService, ChangeFlowsEnergySourcesSelectElementService, ChangeFlowsProductSelectOptionElementService, ClimateService, CspReportService, DynamicDataTableService, FlexAppPowerOfAttorneyService, FlexAppService, FlowsAcquisitionBusinessCheckoutService, FlowsAcquisitionPrivateCheckoutService, FlowsChangeContractCheckoutService, FlowsContractDetailsSectionElementService, FlowsMoveAcquisitionPrivateSwitchService, FlowsMoveBusinessCheckoutService, FlowsMovePrivateCheckoutService, FlowsNewContractDetailsSectionElementService, FlowsPremiseContractSummaryElementService, FlowsPremisesSelectElementService, FlowsProductAddonsSectionViewElementService, FlowsSubmitRenewalOrderElementService, HistoricalSpotPriceService, MOCKUP_DATA, MyConsumptionElementAnonymousService, MyConsumptionElementService, MyContactNotificationDialogueElementService, MyContractsElementService, MyContractsViewElementService, MyEnergyElementService, MyInvoicesViewElementService, MyPagesLoginPageService, MyPoaConsumptionElementService, MyPremisesSelectElementService, MyProfileCommunicationSettingsViewElementService, MyProfileDetailsViewElementService, MyProfileElementService, MyWidgetsConsumptionOverviewElementService, MyWidgetsContractPriceElementService, MyWidgetsElectricityPriceNowElementService, MyWidgetsInvoiceOverviewElementService, MyWidgetsPremiseDetailsElementService, OutageSubscriptionDialogElementService, PostalCodeGridAreaElementService, RenewalFlowsCheckoutSectionService, RenewalFlowsEnergySourcesSelectElementService, RenewalFlowsProductSelectOptionElementService, SelfServiceModule, VisitorGroupCriteriaService };
//# sourceMappingURL=vattenfall-self-service.js.map
